import React, { useMemo, useState, useEffect, useRef } from "react";
import { format, startOfWeek, endOfWeek, addDays, isSameDay, startOfDay } from "date-fns";
import { SchedulingConfig, SchedulingToolUtils } from "../../utils/schedulingToolConfig";
import { IncludeDeptType, ModuleName } from "../../enums";
import { DayPicker, DayButtonProps, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
    ScheduleV3Group,
    ScheduleV3OpenShift,
    SchedulesV3GroupByType,
    ScheduleV3Schedule,
    ScheduleV3Timeoff,
    ScheduleV3Unavailability,
    ScheduleV3Holiday,
    AddScheduleRequest,
    Employee,
} from "../../types";
import { DateTimeUtil } from "../../utils";
import { buildConflictIndex, getConflictSeverity } from "./schedulingToolConflictUtils";
import {
    ArrowUpDown,
    Building,
    UserCheck,
    Users,
    Plus,
    X,
    Calendar as CalendarIcon,
    Loader2,
    Trash2,
    AlertTriangle,
    CheckCircle,
    CheckCircle2,
    Pencil,
    XCircle,
    Clock,
    ChevronDown,
    ChevronRight,
    Copy,
    Clipboard,
    Tag,
    Check,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import {
    addSchedule,
    addScheduleV2,
    updateSchedule,
    updateScheduleV2,
    getSchedulesV3,
    deleteSchedule,
    deleteScheduleV2,
    USE_ADD_SCHEDULE_V2,
    USE_UPDATE_SCHEDULE_V2,
    USE_DELETE_SCHEDULE_V2,
    getPublishedShiftsForAssign,
    assignOpenShiftToEmployee,
} from "../../redux/features/scheduleV3";
import { getMyScheduleDetails } from "../../redux/features/schedule/ScheduleApi";
import { UpdateScheduleV2Request } from "../../redux/features/scheduleV3/scheduleV3Api";
import { searchEmployee, fetchScheduleAttendanceTags, getSitesV2 } from "../../redux/features/common/api";
import { toast } from "react-toastify";
import { cleanErrorMessage } from "../../utils/helper";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { PublishedShiftForAssign } from "../../types";
import ColorPickerWithPalette from "../../components/common/ColorPickerWithPalette";
import { getSchedulingToolShiftColor, setSchedulingToolShiftColor } from "../../utils";
import {
    UnavailabilityDetailModal,
    AddShiftModal,
    SuccessNotification,
    DeleteConfirmationModal,
    ShowMoreModal,
    AssignOpenShiftModal,
    TimeoffDetailModal,
    OpenShiftDetailModal,
} from "./components";
import { updateUnavailability } from "../../redux/features/unavailability/api";
import { deleteTimesheet } from "../../redux/features/timesheet/api";
import { updateTimeoffRequestLog } from "../../redux/features/timeoff/api";
import { showScheduleResultToast } from "./schedulingToolToastUtils";

type SchedulingViewType = "daily" | "weekly" | "monthly";

type EventDisplayField = 
    | 'employeeName'
    | 'site'
    | 'attendanceTag'
    | 'hours'
    | 'time'
    | 'scheduleType'
    | 'department'
    | 'jobCode'
    | 'status'
    | 'notes'
    | 'groupName';

interface EventDisplayConfig {
    schedule: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    timeoff: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    unavailability: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
    openShift: {
        card: EventDisplayField[];
        hover: EventDisplayField[];
    };
}

interface SchedulingToolWeeklyGridProps {
    currentDate: Date;
    viewType: SchedulingViewType;
    groupingType: SchedulesV3GroupByType;
    groups: ScheduleV3Group[];
    onScheduleAdded?: () => void;
    schedulingConfig: Required<SchedulingConfig>;
    hasScheduleToolPrivilege: boolean;
    includeDept: IncludeDeptType;
    showHoliday?: boolean;
    onToggleHoliday?: (show: boolean) => void;
    eventDisplayConfig?: EventDisplayConfig;
    // Grouping dropdown props
    onGroupingTypeChange?: (value: SchedulesV3GroupByType) => void;
    getGroupingOptions?: () => Array<{ value: string; label: string; icon: string }>;
    sidebarSortBy?: string;
    getSortOptions?: () => Array<{ value: string; label: string }>;
    onSidebarSortChange?: (value: string) => void;
    selectedTagCode?: string | null;
    onTagToggle?: (tagCode: string) => void;
    attendanceTags?: any[];
    isLoadingAttendanceTags?: boolean;
    isAttendanceTagsDropdownOpen?: boolean;
    onAttendanceTagsDropdownToggle?: (open: boolean) => void;
    /** When manager clicks Publish in open shift detail modal, main opens publish confirmation */
    onPublishOpenShift?: () => void;
    /** Called after open shift is successfully cancelled – parent should refresh grid data */
    onCancelShiftSuccess?: () => void;
}

// Converts a bare "HH:mm[:ss]" time into a parseable datetime string (dayjs.utc can't parse bare times);
// datetime strings ("...T...", "... ...") are passed through unchanged.
function toParsableDateTime(timeStr?: string | null): string | null {
    if (!timeStr) return null;
    if (timeStr.includes('T') || timeStr.includes(' ')) return timeStr;
    const withSeconds = timeStr.length === 5 && timeStr.includes(':') ? `${timeStr}:00` : timeStr;
    return `2000-01-01 ${withSeconds}`;
}

function resolveShiftTimeRange(startRaw?: string | null, endRaw?: string | null): string {
    if (!startRaw || !endRaw) return '';
    const start = toParsableDateTime(startRaw);
    const end = toParsableDateTime(endRaw);
    const formatted = DateTimeUtil.formatElegantTimeRange(start, end);
    if (formatted && formatted !== 'N/A') return formatted;
    return `${startRaw}-${endRaw}`;
}

const SchedulingToolWeeklyGrid: React.FC<SchedulingToolWeeklyGridProps> = ({
    currentDate,
    viewType,
    groupingType,
    groups,
    onScheduleAdded,
    schedulingConfig,
    hasScheduleToolPrivilege,
    includeDept,
    showHoliday = true,
    onToggleHoliday,
    eventDisplayConfig,
    onGroupingTypeChange,
    getGroupingOptions,
    sidebarSortBy,
    getSortOptions,
    onSidebarSortChange,
    selectedTagCode = null,
    onTagToggle,
    attendanceTags = [],
    isLoadingAttendanceTags = false,
    isAttendanceTagsDropdownOpen = false,
    onAttendanceTagsDropdownToggle,
    onPublishOpenShift,
    onCancelShiftSuccess,
}) => {
    const dispatch = useAppDispatch();
    const { sitesV2, departments, jobCodes, shifts, employeeList } = useSelector(
        (state: RootState) => state.common
    );
    const user = useSelector((state: RootState) => state.user.user);

    // Default config if not provided
    const defaultConfig: EventDisplayConfig = {
        schedule: {
            card: ['time', 'employeeName', 'scheduleType'],
            hover: ['employeeName', 'scheduleType', 'time', 'hours', 'site', 'department', 'notes'],
        },
        timeoff: {
            card: ['time', 'employeeName', 'scheduleType'],
            hover: ['employeeName', 'scheduleType', 'time', 'hours', 'notes'],
        },
        unavailability: {
            card: ['time', 'employeeName'],
            hover: ['employeeName', 'time', 'notes'],
        },
        openShift: {
            card: ['time', 'scheduleType'],
            hover: ['scheduleType', 'time', 'status', 'site', 'department', 'notes'],
        },
    };

    const displayConfig = eventDisplayConfig || defaultConfig;

    const getTooltipPlacementClasses = (
        openBelow: boolean,
        align: "left" | "center" = "left"
    ) => {
        if (openBelow) {
            return align === "center"
                ? "top-full left-1/2 mt-2 -translate-x-1/2"
                : "top-full left-0 mt-2";
        }

        return align === "center"
            ? "bottom-full left-1/2 mb-2 -translate-x-1/2"
            : "bottom-full left-0 mb-2";
    };

    const getTooltipArrowClasses = (openBelow: boolean) => {
        return openBelow
            ? "absolute bottom-full left-1/2 -translate-x-1/2"
            : "absolute top-full left-1/2 -translate-x-1/2 -mt-1";
    };

    const getTooltipArrowInnerClasses = (openBelow: boolean) => {
        return openBelow
            ? "border-4 border-transparent border-b-gray-800"
            : "border-4 border-transparent border-t-gray-800";
    };

    // Helper function to get field value
    const getFieldValue = (
        field: EventDisplayField,
        data: {
            schedule?: ScheduleV3Schedule;
            timeoff?: ScheduleV3Timeoff;
            unavailability?: ScheduleV3Unavailability;
            openShift?: any;
            groupName?: string;
            hours?: number;
            timeRange?: string;
        },
        eventType: 'schedule' | 'timeoff' | 'unavailability' | 'openShift'
    ): string => {
        switch (field) {
            case 'employeeName':
                if (eventType === 'schedule') return data.schedule?.employee?.name || '';
                if (eventType === 'timeoff') return data.timeoff?.employee?.name || '';
                if (eventType === 'unavailability') {
                    return (typeof data.unavailability?.employee?.name === 'string' && data.unavailability.employee.name) ||
                           (typeof (data.unavailability as any)?.employee_name === 'string' && (data.unavailability as any).employee_name) ||
                           '';
                }
                if (eventType === 'openShift') {
                    const originalEmployee = (data.openShift as any)?.original_employee;
                    if (originalEmployee?.name) return String(originalEmployee.name);
                    if (originalEmployee?.text) return String(originalEmployee.text);
                    return (data.openShift as any)?.manager_details?.name || '';
                }
                return '';
            case 'site':
                if (eventType === 'schedule') {
                    const site = data.schedule?.site;
                    return site?.site_name || site?.site_code || '';
                }
                if (eventType === 'openShift') {
                    return (
                        data.openShift?.site?.site_name ||
                        data.openShift?.site?.site_code ||
                        data.openShift?.shift_details?.site_name ||
                        ''
                    );
                }
                return '';
            case 'attendanceTag':
                if (eventType === 'schedule' && data.schedule) {
                    const schedule = data.schedule as any;
                    // Check multiple possible locations for attendance tags
                    let tags = schedule.tags || 
                               schedule.attendance_tags || 
                               schedule.shift_attributes?.attendance_tags || 
                               (schedule as any).attendanceTags;
                    
                    if (tags) {
                        if (Array.isArray(tags)) {
                            const tagValues: string[] = [];
                            
                            tags.forEach((tag: any) => {
                                // Handle the new format: tags with field_values array
                                if (tag.field_values && Array.isArray(tag.field_values)) {
                                    tag.field_values.forEach((fv: any) => {
                                        if (fv.value_text) {
                                            tagValues.push(fv.value_text);
                                        } else if (fv.value_code) {
                                            tagValues.push(fv.value_code);
                                        }
                                    });
                                }
                                // Handle old format: direct tag objects
                                else if (typeof tag === 'string') {
                                    tagValues.push(tag);
                                } else if (tag.value_text) {
                                    tagValues.push(tag.value_text);
                                } else if (tag.value) {
                                    tagValues.push(tag.value);
                                } else if (tag.tag_name) {
                                    tagValues.push(tag.tag_name);
                                } else if (tag.name) {
                                    tagValues.push(tag.name);
                                } else if (tag.code) {
                                    tagValues.push(tag.code);
                                }
                            });
                            
                            return tagValues.length > 0 ? tagValues.join(', ') : '';
                        }
                    }
                }
                if (eventType === 'openShift') {
                    const shiftTags = (data.openShift as any)?.shift_tags;
                    if (!Array.isArray(shiftTags)) return '';
                    const values: string[] = [];
                    shiftTags.forEach((tag: any) => {
                        if (Array.isArray(tag?.field_values)) {
                            tag.field_values.forEach((fv: any) => {
                                const text = fv?.value_text || fv?.value_code;
                                if (text) values.push(String(text));
                            });
                        }
                    });
                    return values.join(', ');
                }
                return '';
            case 'hours':
                if (data.hours !== undefined) return `${data.hours}h`;
                if (eventType === 'schedule' && data.schedule?.shift) {
                    const hours = getShiftDurationHours(data.schedule.shift.start_time, data.schedule.shift.end_time);
                    return hours > 0 ? `${hours.toFixed(1)}h` : '';
                }
                if (eventType === 'timeoff' && data.timeoff?.hours) {
                    return `${data.timeoff.hours}h`;
                }
                return '';
            case 'time':
                if (data.timeRange && data.timeRange !== 'N/A') return data.timeRange;
                if (eventType === 'schedule' && data.schedule?.shift) {
                    return resolveShiftTimeRange(data.schedule.shift.start_time, data.schedule.shift.end_time);
                }
                return '';
            case 'scheduleType':
                if (eventType === 'schedule') return data.schedule?.shift?.shift_name || '';
                if (eventType === 'timeoff') return data.timeoff?.earnings_code_name || '';
                if (eventType === 'openShift') return data.openShift?.shift_details?.shift_name || 'Open Shift';
                return '';
            case 'department':
                if (eventType === 'schedule' && data.schedule) {
                    const schedule = data.schedule as any;
                    if (schedule.department) {
                        return schedule.department.department_name || 
                               schedule.department.dept_name || 
                               schedule.department.department_code || 
                               schedule.department.dept_code || 
                               '';
                    }
                }
                if (eventType === 'openShift') {
                    return (
                        data.openShift?.department?.department_name ||
                        data.openShift?.department?.department_code ||
                        data.openShift?.shift_details?.department_name ||
                        ''
                    );
                }
                return '';
            case 'status':
                if (eventType === 'openShift') {
                    return data.openShift?.status ? String(data.openShift.status) : '';
                }
                return '';
            case 'jobCode':
                if (eventType === 'schedule' && data.schedule) {
                    const schedule = data.schedule as any;
                    if (schedule.job_code) {
                        return schedule.job_code.job_code_name || schedule.job_code.job_code_code || '';
                    }
                }
                return '';
            case 'notes':
                if (eventType === 'schedule') {
                    const notes = data.schedule?.notes;
                    return (notes && notes !== 'null') ? notes : '';
                }
                if (eventType === 'timeoff') {
                    return (data.timeoff as any)?.note || data.timeoff?.notes || '';
                }
                if (eventType === 'unavailability') {
                    return (data.unavailability as any)?.note || (data.unavailability as any)?.reason || '';
                }
                if (eventType === 'openShift') {
                    return data.openShift?.notes || '';
                }
                return '';
            case 'groupName':
                return data.groupName || '';
            default:
                return '';
        }
    };

    // Helper to format card content
    const formatCardContent = (
        fields: EventDisplayField[],
        data: any,
        eventType: 'schedule' | 'timeoff' | 'unavailability' | 'openShift',
        groupName: string
    ): string[] => {
        return fields
            .map(field => getFieldValue(field, { ...data, groupName }, eventType))
            .filter(Boolean);
    };

    // Helper to format hover content
    const formatHoverContent = (
        fields: EventDisplayField[],
        data: any,
        eventType: 'schedule' | 'timeoff' | 'unavailability' | 'openShift',
        groupName: string
    ): string => {
        const parts = fields
            .map(field => {
                const value = getFieldValue(field, { ...data, groupName }, eventType);
                if (!value) return null;
                const label = field === 'employeeName' ? 'Employee' :
                             field === 'scheduleType' ? 'Type' :
                             field === 'attendanceTag' ? 'Att. Tag' :
                             field === 'jobCode' ? 'Job Code' :
                             field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
                return `${label}: ${value}`;
            })
            .filter(Boolean);
        return parts.join(' | ');
    };

    // Get days to display based on view type
    const displayDays = useMemo(() => {
        if (viewType === "daily") {
            return [startOfDay(currentDate)];
        } else if (viewType === "weekly") {
            const weekStart = SchedulingToolUtils.getWeekStart(currentDate, schedulingConfig);
            return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
        } else {
            // Monthly view - show all days in the month
            const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            const daysInMonth = endOfMonth.getDate();
            return Array.from({ length: daysInMonth }, (_, i) => addDays(startOfMonth, i));
        }
    }, [currentDate, viewType, schedulingConfig]);

    const conflictsByScheduleId = useMemo(() => buildConflictIndex(groups), [groups]);

    // Get icon based on grouping type
    const getDefaultGroupingIcon = () => {
        switch (groupingType) {
            case "department":
                return <Building className="w-4 h-4" />;
            case "site":
                return <Building className="w-4 h-4" />;
            case "employee":
                return <span aria-hidden>👤</span>;
            case "tags":
                return <CalendarIcon className="w-4 h-4" />;
            case "job_code":
                return <UserCheck className="w-4 h-4" />;
            default:
                return <UserCheck className="w-4 h-4" />;
        }
    };

    const groupHasOnlyOpenShifts = (group: ScheduleV3Group): boolean => {
        const dates = group?.dates ? Object.values(group.dates) : [];
        let hasAnyOpenShifts = false;

        for (const dateData of dates as any[]) {
            const openShifts = Array.isArray(dateData?.open_shifts) ? dateData.open_shifts : [];
            const schedules = Array.isArray(dateData?.schedules) ? dateData.schedules : [];
            const timeoff = Array.isArray(dateData?.timeoff) ? dateData.timeoff : [];
            const unavailability = Array.isArray(dateData?.unavailability) ? dateData.unavailability : [];
            const holiday = Array.isArray(dateData?.holiday) ? dateData.holiday : [];

            if (openShifts.length > 0) hasAnyOpenShifts = true;
            if (
                schedules.length > 0 ||
                timeoff.length > 0 ||
                unavailability.length > 0 ||
                holiday.length > 0
            ) {
                return false;
            }
        }

        return hasAnyOpenShifts;
    };

    const getDisplayGroupName = (group: ScheduleV3Group): string => {
        if (
            group?.group_id === "ungrouped_tags" &&
            group?.group_name === "Unassigned Tags" &&
            groupHasOnlyOpenShifts(group)
        ) {
            return "Open Shifts";
        }
        return group?.group_name || "";
    };

    const getGroupIconForGroup = (group: ScheduleV3Group) => {
        const id = String(group?.group_id || "").toLowerCase();
        const name = String(group?.group_name || "").toLowerCase();
        const displayName = getDisplayGroupName(group);

        const isUnavailabilityRow = id.includes("unavailability") || name.includes("unavail");
        if (isUnavailabilityRow) return <span aria-hidden>🚫</span>;

        const isTimeoffRow = id.includes("timeoff") || name.includes("time off") || name.includes("timeoff");
        if (isTimeoffRow) return <span aria-hidden>📅</span>;

        const isOpenShiftsRow = id.includes("open_shifts") || displayName === "Open Shifts";
        if (isOpenShiftsRow) return <span aria-hidden>🕒</span>;

        return getDefaultGroupingIcon();
    };

    const timeStringToSeconds = (value?: string | null): number | null => {
        if (!value) return null;

        const trimmed = value.trim();
        if (!trimmed) return null;

        const meridiemMatch = trimmed.match(/\s?(am|pm)$/i);
        let timePart = trimmed;
        let meridiem: "AM" | "PM" | null = null;

        if (meridiemMatch) {
            meridiem = meridiemMatch[1].toUpperCase() as "AM" | "PM";
            timePart = trimmed.replace(/\s?(am|pm)$/i, "").trim();
        }

        const [hoursStr = "0", minutesStr = "0", secondsStr = "0"] = timePart.split(":");
        let hours = Number(hoursStr);
        const minutes = Number(minutesStr);
        const seconds = Number(secondsStr);

        if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
            return null;
        }

        if (meridiem) {
            hours = hours % 12;
            if (meridiem === "PM") {
                hours += 12;
            }
        }

        return hours * 3600 + minutes * 60 + seconds;
    };

    const getShiftDurationHours = (startTime?: string | null, endTime?: string | null): number => {
        if (!startTime || !endTime) return 0;

        const startInSeconds = timeStringToSeconds(startTime);
        const endInSeconds = timeStringToSeconds(endTime);

        if (startInSeconds === null || endInSeconds === null) {
            return 0;
        }

        let diffInSeconds = endInSeconds - startInSeconds;

        if (diffInSeconds < 0) {
            diffInSeconds += 24 * 3600;
        }

        return diffInSeconds / 3600;
    };

    // Pretty print time for unavailability display
    const formatUnavailabilityTime = (value?: string | null): string => {
        if (!value) return "";
        return DateTimeUtil.prettyPrintTime(value.trim());
    };

    // Pretty print time range for unavailability display
    const formatUnavailabilityTimeRange = (start?: string | null, end?: string | null): string => {
        if (!start || !end || typeof start !== 'string' || typeof end !== 'string') {
            return "All day";
        }
        const trimmedStart = start.trim();
        const trimmedEnd = end.trim();
        if (!trimmedStart || !trimmedEnd) {
            return "All day";
        }
        const result = DateTimeUtil.prettyPrintTimeRange(trimmedStart, trimmedEnd);
        // If pretty print returns "N/A", fall back to showing raw times or "All day"
        if (result === "N/A" || result === "Invalid time") {
            return "All day";
        }
        return result;
    };

    /** Resolve time-off request ID for opening detail modal. Prefer TIME_OFF_REQUEST_ID; if missing and schedules_id is empty, use timeoff_id. */
    const getEffectiveTimeoffRequestId = (timeoff: ScheduleV3Timeoff): string | null => {
        const requestId = timeoff.other_details?.TIME_OFF_REQUEST_ID;
        const hasRequestId = requestId != null && String(requestId).trim() !== "";
        if (hasRequestId) return String(requestId).trim();
        const schedulesId = timeoff.schedules_id ?? null;
        const schedulesIdEmpty = schedulesId == null || String(schedulesId).trim() === "";
        if (schedulesIdEmpty && timeoff.timeoff_id != null && String(timeoff.timeoff_id).trim() !== "") {
            return String(timeoff.timeoff_id).trim();
        }
        return null;
    };

    // Format date string (YYYY-MM-DD) to readable format without parsing to Date object
    // This ensures dates are displayed exactly as-is without any timezone conversion
    const formatDateString = (dateStr: string): string => {
        // Format YYYY-MM-DD to "Month Day, Year" format without parsing
        const [year, month, day] = dateStr.split('-');
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[parseInt(month, 10) - 1] || month;
        const dayNum = parseInt(day, 10);
        return `${monthName} ${dayNum}, ${year}`;
    };


    // Calculate total hours for a group
    const calculateGroupHours = (group: ScheduleV3Group): number => {
        let totalHours = 0;

        Object.values(group.dates).forEach((dateData) => {
            dateData.schedules?.forEach((schedule) => {
                totalHours += getShiftDurationHours(schedule.shift?.start_time, schedule.shift?.end_time);
            });
        });

        return totalHours;
    };

    // Get schedules for a specific group and day
    const getSchedulesForGroupAndDay = (group: ScheduleV3Group, date: Date) => {
        const dateStr = format(date, "yyyy-MM-dd");
        const dateData = group.dates[dateStr];

        if (!dateData || !dateData.schedules) {
            return [];
        }

        // Create a copy before sorting to avoid mutating Redux state
        return [...dateData.schedules].sort((a, b) => {
            if (a.shift?.start_time && b.shift?.start_time) {
                return a.shift.start_time.localeCompare(b.shift.start_time);
            }
            return 0;
        });
    };

    // Generate placeholder groups when no groups exist (to show empty rows for adding schedules)
    const getPlaceholderGroups = useMemo((): ScheduleV3Group[] => {
        const placeholderGroups: ScheduleV3Group[] = [];

        switch (groupingType) {
            case "department":
                departments.forEach((dept) => {
                    if (dept.department_code && dept.department_name) {
                        placeholderGroups.push({
                            group_id: dept.department_code,
                            group_name: dept.department_name,
                            dates: {},
                        });
                    }
                });
                break;
            case "site":
                sitesV2.forEach((site) => {
                    const siteId = site.us_user_site_profile_id?.toString() || site.us_site_code || "";
                    const siteName = site.user_site_name || "";
                    if (siteId && siteName) {
                        placeholderGroups.push({
                            group_id: siteId,
                            group_name: siteName,
                            dates: {},
                        });
                    }
                });
                break;
            case "employee": {
                if (includeDept === IncludeDeptType.MY_SCHEDULE) {
                    const currentUserId =
                        user?.os_emp_account_id?.toString() ||
                        user?.employee_id?.toString() ||
                        user?.id?.toString() ||
                        "";

                    const employeeName = user?.employee_name;
                    const firstName =
                        user?.osEmpFirstName ||
                        user?.os_emp_first_name ||
                        (employeeName ? employeeName.split(" ")[0] : "");
                    const lastName =
                        user?.osEmpLastName ||
                        user?.os_emp_last_name ||
                        (employeeName && employeeName.includes(" ")
                            ? employeeName.split(" ").slice(1).join(" ")
                            : "");
                    const currentUserName =
                        employeeName ||
                        `${firstName} ${lastName}`.trim() ||
                        firstName ||
                        "My Schedule";

                    if (currentUserId) {
                        placeholderGroups.push({
                            group_id: currentUserId,
                            group_name: currentUserName,
                            dates: {},
                        });
                    }
                } else {
                    employeeList.forEach((emp) => {
                        const empId = emp.employee_id?.toString() || "";
                        const empName =
                            emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Unknown";
                        if (empId && empName) {
                            placeholderGroups.push({
                                group_id: empId,
                                group_name: empName,
                                dates: {},
                            });
                        }
                    });
                }
                break;
            }
            case "job_code":
                jobCodes.forEach((jobCode) => {
                    const jobCodeId = jobCode.os_work_codes_id?.toString() || "";
                    const jobCodeName = jobCode.work_code_name || "";
                    if (jobCodeId && jobCodeName) {
                        placeholderGroups.push({
                            group_id: jobCodeId,
                            group_name: jobCodeName,
                            dates: {},
                        });
                    }
                });
                break;
        }

        return placeholderGroups;
    }, [groupingType, departments, sitesV2, employeeList, jobCodes, includeDept, user]);

    const getMonthlyDepartmentGroup = React.useCallback((): ScheduleV3Group | undefined => {
        if (viewType !== "monthly" || groupingType !== "department") {
            return undefined;
        }

        const departmentGroups = groups.filter((group) => {
            const groupId = String(group.group_id || "");
            return (
                groupId &&
                !groupId.startsWith("ungrouped_") &&
                !groupId.includes("_timeoff") &&
                !groupId.includes("_unavailability") &&
                !groupId.includes("_holiday") &&
                !groupId.includes("_open_shifts")
            );
        });
        const userDepartmentCode = user?.department_code ? String(user.department_code) : "";

        if (
            userDepartmentCode &&
            (includeDept === IncludeDeptType.MY_DEPARTMENT || includeDept === IncludeDeptType.MY_SCHEDULE)
        ) {
            return (
                departmentGroups.find((group) => String(group.group_id) === userDepartmentCode) || {
                    group_id: userDepartmentCode,
                    group_name:
                        departments.find((dept) => String(dept.department_code) === userDepartmentCode)
                            ?.department_name || userDepartmentCode,
                    dates: {},
                }
            );
        }

        return departmentGroups.length === 1 ? departmentGroups[0] : undefined;
    }, [viewType, groupingType, groups, user?.department_code, includeDept, departments]);

    // State for selected day in monthly view
    const [selectedDay, setSelectedDay] = useState<Date | undefined>();

    // State for sort dropdown (minimalistic icon button)
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
    const sortDropdownRef = useRef<HTMLDivElement>(null);

    // State for add shift modal
    const [isAddShiftModalOpen, setIsAddShiftModalOpen] = useState(false);
    const [selectedDateForShift, setSelectedDateForShift] = useState<Date | null>(null);
    const [hoveredCell, setHoveredCell] = useState<string | null>(null);
    const [suppressedShiftTooltipKey, setSuppressedShiftTooltipKey] = useState<string | null>(null);
    const [showOptionalFields, setShowOptionalFields] = useState(false);
    
    // Close tags dropdown when grouping type changes (if not grouping by tags)
    useEffect(() => {
        if (groupingType !== 'tags' && isAttendanceTagsDropdownOpen && onAttendanceTagsDropdownToggle) {
            onAttendanceTagsDropdownToggle(false);
        }
    }, [groupingType, isAttendanceTagsDropdownOpen, onAttendanceTagsDropdownToggle]);

    // Close sort dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
                setIsSortDropdownOpen(false);
            }
        };
        if (isSortDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSortDropdownOpen]);

    // Fetch schedule attendance tags when optional fields are opened
    useEffect(() => {
        if (showOptionalFields) {
            dispatch(fetchScheduleAttendanceTags());
        }
    }, [showOptionalFields, dispatch]);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState<ScheduleV3Schedule | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteConfirmationStep, setDeleteConfirmationStep] = useState(1);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // Show more modal state
    const [showMoreModal, setShowMoreModal] = useState(false);
    const [showMoreDate, setShowMoreDate] = useState<Date | null>(null);
    const [showMoreItems, setShowMoreItems] = useState<{
        schedules: Array<{ schedule: ScheduleV3Schedule; groupName: string; group: ScheduleV3Group }>;
        timeoffs: Array<{ timeoff: ScheduleV3Timeoff; groupName: string; group: ScheduleV3Group }>;
        unavailability?: Array<{ unavailability: ScheduleV3Unavailability; groupName: string; group: ScheduleV3Group }>;
        openShifts: Array<{ openShift: any; groupName: string; group: ScheduleV3Group }>;
    }>({ schedules: [], timeoffs: [], openShifts: [] });

    // Copy/Paste state
    const [copiedSchedule, setCopiedSchedule] = useState<{
        schedule: ScheduleV3Schedule;
        formData: any;
        attendanceFormData: any;
    } | null>(() => {
        // Load from localStorage on mount
        const saved = localStorage.getItem("scheduling_tool_copied_schedule");
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch {
                return null;
            }
        }
        return null;
    });
    const pasteModeToastIdRef = useRef<any>(null);

    // Assign open shift modal state
    const [showAssignOpenShiftModal, setShowAssignOpenShiftModal] = useState(false);
    const [selectedOpenShift, setSelectedOpenShift] = useState<any>(null);
    // Open shift detail modal (claim / publish / assign)
    const [openShiftDetailModalOpen, setOpenShiftDetailModalOpen] = useState(false);
    const [openShiftDetailModalShiftId, setOpenShiftDetailModalShiftId] = useState<string | null>(null);
    const [openShiftDetailModalInitial, setOpenShiftDetailModalInitial] = useState<ScheduleV3OpenShift | null>(null);
    const [openShiftDetailModalDay, setOpenShiftDetailModalDay] = useState<Date | undefined>();
    const [openShiftDetailModalGroup, setOpenShiftDetailModalGroup] = useState<ScheduleV3Group | undefined>();

    // Track last schedule id we fetched details for (to avoid duplicate fetches while modal is open)
    const [lastScheduleDetailId, setLastScheduleDetailId] = useState<string | null>(null);
    
    // Sync holidays row expansion with showHoliday prop
    const isHolidaysRowExpanded = showHoliday;
    const [selectedEmployeeForOpenShift, setSelectedEmployeeForOpenShift] = useState<any>(null);
    const [isAssigningOpenShift, setIsAssigningOpenShift] = useState(false);
    const [publishedShiftForAssign, setPublishedShiftForAssign] = useState<PublishedShiftForAssign | null>(null);
    const [isLoadingPublishedShift, setIsLoadingPublishedShift] = useState(false);

    // Unavailability modal state
    const [isUnavailabilityModalOpen, setIsUnavailabilityModalOpen] = useState(false);
    const [isWithdrawingUnavailability, setIsWithdrawingUnavailability] = useState(false);
    const [selectedUnavailability, setSelectedUnavailability] = useState<{
        unavail: any;
        employeeName: string;
        timeRange: string;
        isFullDay: boolean;
        unavailType: string;
    } | null>(null);

    // Timeoff detail modal state
    const [isTimeoffDetailModalOpen, setIsTimeoffDetailModalOpen] = useState(false);
    const [isWithdrawingTimeoff, setIsWithdrawingTimeoff] = useState(false);
    const [selectedTimeoffForModal, setSelectedTimeoffForModal] = useState<{
        timeoff: ScheduleV3Timeoff;
        groupName: string;
        time_off_request_id: string;
    } | null>(null);

    // Constants
    const MAX_VISIBLE_ITEMS_MONTHLY = 3; // Show max 3 items per cell in monthly view
    const MAX_VISIBLE_ITEMS_WEEKLY = 5; // Show max 5 items per cell in weekly/daily view

    // Form state
    const [formData, setFormData] = useState<AddScheduleRequest>({
        singleuser: 1,
        occurence_type: "DAY",
        add_days: 1,
        start_from_custom: 0,
        shift_code: "",
    });

    // Free flow state (similar to mass assignment)
    const [onlyNotes, setOnlyNotes] = useState(false);
    const [freeFlowStartDateTime, setFreeFlowStartDateTime] = useState("");
    const [freeFlowEndDateTime, setFreeFlowEndDateTime] = useState("");
    const [freeFlowShiftName, setFreeFlowShiftName] = useState("");
    const [selectedEmployeeOptions, setSelectedEmployeeOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [selectedDepartmentCode, setSelectedDepartmentCode] = useState<string | null>(null);
    const allowCustomShift = !schedulingConfig.RESTRICT_TO_PREDEFINED_SHIFTS;

    // Helpers for free-flow datetime handling
    const formatDateTimeLocal = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");
    const toMysqlDateTime = (value: string) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return DateTimeUtil.formatDateToMySQL(date);
    };

    const handleFreeFlowStartChange = (value: string) => {
        setFreeFlowStartDateTime(value);

        if (!value) {
            setFreeFlowEndDateTime("");
            return;
        }

        const startDate = new Date(value);
        if (Number.isNaN(startDate.getTime())) return;

        const existingEnd = freeFlowEndDateTime ? new Date(freeFlowEndDateTime) : null;
        const needsAutoEnd = !freeFlowEndDateTime || (existingEnd && existingEnd < startDate);

        if (needsAutoEnd) {
            const autoEnd = new Date(startDate.getTime() + 60 * 60 * 1000); // default +1 hour
            setFreeFlowEndDateTime(formatDateTimeLocal(autoEnd));
        }
    };

    const handleFreeFlowEndChange = (value: string) => {
        if (!value) {
            setFreeFlowEndDateTime("");
            return;
        }

        const startDate = freeFlowStartDateTime ? new Date(freeFlowStartDateTime) : null;
        const endDate = new Date(value);

        // Allow overnight shifts: if end is earlier than start, roll it to next day with same time
        if (startDate && !Number.isNaN(startDate.getTime()) && endDate < startDate) {
            const overnightEnd = new Date(startDate);
            overnightEnd.setDate(overnightEnd.getDate() + 1);
            overnightEnd.setHours(endDate.getHours(), endDate.getMinutes(), endDate.getSeconds(), endDate.getMilliseconds());
            setFreeFlowEndDateTime(formatDateTimeLocal(overnightEnd));
            return;
        }

        setFreeFlowEndDateTime(value);
    };

    const getEmployeeOptionById = React.useCallback(
        (idValue?: string | number | null, fallbackName?: string | null) => {
            if (idValue === undefined || idValue === null) return null;
            const idStr = String(idValue);
            const employee = employeeList.find((emp) => String(emp.employee_id) === idStr);
            const label =
                employee?.full_name ||
                (employee?.first_name || employee?.last_name
                    ? `${employee?.first_name ?? ""} ${employee?.last_name ?? ""}`.trim()
                    : fallbackName || idStr);
            return {
                value: idStr,
                label,
            };
        },
        [employeeList]
    );

    const searchDebounceRef = useRef<number>();
    const pendingSearchRef = useRef<Promise<any> | null>(null);

    const loadEmployeeOptionsForSchedule = async (inputValue: string, departmentCodeOverride?: string) => {
        if (inputValue.length < 3) {
            if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = undefined;
            }
            pendingSearchRef.current = null;
            return selectedEmployeeOptions;
        }

        if (searchDebounceRef.current) {
            window.clearTimeout(searchDebounceRef.current);
        }

        return new Promise<{ value: string; label: string }[]>((resolve) => {
            searchDebounceRef.current = window.setTimeout(async () => {
                const departmentCode =
                    departmentCodeOverride ?? selectedDepartmentCode ?? "";
                const currentSearch = dispatch(
                    searchEmployee({
                        search: inputValue,
                        departments: departmentCode,
                        groups: "",
                        site_id: "",
                        page: 1,
                        module_name: ModuleName.EMPLOYEE_PRIV_SCHEDULE_TOOL,
                    })
                ).unwrap();

                pendingSearchRef.current = currentSearch;

                try {
                    const response = await currentSearch;

                    if (pendingSearchRef.current !== currentSearch) {
                        return resolve([]);
                    }

                    resolve(
                        response.data.employees.map((employee: any) => ({
                            value: employee.os_emp_account_id,
                            label: employee.text,
                        }))
                    );
                } catch (error) {
                    console.error("Error searching employees:", error);
                    resolve([]);
                } finally {
                    if (pendingSearchRef.current === currentSearch) {
                        pendingSearchRef.current = null;
                    }
                }
            }, 300);
        });
    };

    useEffect(() => {
        if (!isAddShiftModalOpen) {
            setSelectedEmployeeOptions([]);
            setSelectedDepartmentCode(null);
        }
    }, [isAddShiftModalOpen]);

    // Load sites when modal opens if not already loaded (do not depend on sitesV2 to avoid update loop)
    useEffect(() => {
        if (isAddShiftModalOpen) {
            const employeeId = localStorage.getItem("employee_id") || user?.os_emp_account_id?.toString() || "";
            if (employeeId && (!sitesV2 || sitesV2.length === 0)) {
                dispatch(getSitesV2({ employee_id: employeeId }));
            }
        }
    }, [isAddShiftModalOpen, dispatch, user?.os_emp_account_id]);

    // Handle ESC key to clear copied schedule
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && copiedSchedule) {
                setCopiedSchedule(null);
                localStorage.removeItem("scheduling_tool_copied_schedule");
                if (pasteModeToastIdRef.current) {
                    toast.dismiss(pasteModeToastIdRef.current);
                    pasteModeToastIdRef.current = null;
                }
                toast.info("Paste mode cleared (ESC pressed)");
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [copiedSchedule]);

    // Debug: Log common state when it changes
    useEffect(() => {
        console.log("Grid - Common state updated:", { sitesV2, attendanceTags, departments, jobCodes, shifts });
    }, [sitesV2, attendanceTags, departments, jobCodes, shifts]);

    // Debug: Log when edit mode changes
    useEffect(() => {
        console.log("isEditMode changed to:", isEditMode);
        console.log("Current editingSchedule:", editingSchedule);
    }, [isEditMode, editingSchedule]);

    // Note: Employees are now fetched in the parent component (schedulingToolMain.tsx)
    // based on the grouping type and view settings. No need to fetch here.

    // Handle cell click to open add shift modal
    const handleCellClick = (date: Date, group?: ScheduleV3Group, event?: React.MouseEvent) => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to create schedules");
            return;
        }

        // Check if editing is allowed for this date
        if (!SchedulingToolUtils.canEditDate(date, schedulingConfig)) {
            toast.error("Cannot edit shifts for past dates");
            return;
        }

        // If there's a copied schedule, paste it automatically when clicking a cell
        if (copiedSchedule) {
            
            // Paste the copied schedule
            const dateStr = format(date, "yyyy-MM-dd");
            
            // Determine target employee based on grouping
            let targetEmployeeId: number | undefined;
            let targetEmployeeName: string | undefined;
            
            if (groupingType === "employee" && group?.group_id) {
                targetEmployeeId = parseInt(group.group_id);
                targetEmployeeName = group.group_name;
            } else {
                // For other grouping types, use the employee from the group context if available
                // Or keep the original employee if no group context
                targetEmployeeId = copiedSchedule.schedule.employee?.id ? parseInt(copiedSchedule.schedule.employee.id) : undefined;
                targetEmployeeName = copiedSchedule.schedule.employee?.name;
            }
            
            // Get the original schedule times
            const originalSchedule = copiedSchedule.schedule;
            const startTime = originalSchedule.shift?.start_time || "10:00";
            const endTime = originalSchedule.shift?.end_time || "17:00";
            
            // Convert times to datetime format
            const startDateTime = `${dateStr} ${startTime}`;
            const endDateTime = `${dateStr} ${endTime}`;
            
            // Determine shift ID
            const shiftIdValue = originalSchedule.shift?.shift_id;
            const shiftId = shiftIdValue && String(shiftIdValue) !== "0" && shiftIdValue !== 0
                ? parseInt(String(shiftIdValue)) 
                : undefined;
            
            // Determine if free flow
            const isFreeFlow = !shiftId || shiftId === 0;
            
            // Set form data with pasted schedule, but use target date and employee
            // Load saved color preference from shift_planner_settings if not in copied schedule
            const savedColor = getSchedulingToolShiftColor();
            
            // Auto-select department when in department view
            const departmentCode = groupingType === "department" && group?.group_id 
                ? String(group.group_id) 
                : (copiedSchedule.formData.department_code || null);
            
            // Auto-select site when in site view
            let siteId: number | undefined;
            if (groupingType === "site" && group?.group_id) {
                // group_id is the site_code, need to look up site_id from sitesV2
                const siteCode = group.group_id;
                if (sitesV2 && sitesV2.length > 0) {
                    const site = sitesV2.find(s => s.us_site_code === siteCode);
                    if (site && site.us_user_site_profile_id) {
                        siteId = parseInt(site.us_user_site_profile_id.toString());
                    } else {
                        // Fallback: try to parse as integer (for backward compatibility)
                        const parsed = parseInt(siteCode);
                        if (!isNaN(parsed)) {
                            siteId = parsed;
                        }
                    }
                } else {
                    // Fallback: try to parse as integer (for backward compatibility)
                    const parsed = parseInt(siteCode);
                    if (!isNaN(parsed)) {
                        siteId = parsed;
                    }
                }
            } else {
                // Use site from copied schedule if available
                siteId = copiedSchedule.formData.us_user_site_profile_id;
            }
            
            setFormData({
                ...copiedSchedule.formData,
                schedule_date: dateStr,
                os_emp_account_id: targetEmployeeId ? [targetEmployeeId] : copiedSchedule.formData.os_emp_account_id,
                os_emp_start_datetime: startDateTime,
                os_emp_end_datetime: endDateTime,
                os_schedules_site_shifts_id: shiftId,
                shift_color: copiedSchedule.formData.shift_color || savedColor,
                department_code: departmentCode || undefined,
                us_user_site_profile_id: siteId,
            });
            
            // Set employee options
            if (targetEmployeeId && targetEmployeeName) {
                const employeeOption = getEmployeeOptionById(String(targetEmployeeId), targetEmployeeName);
                setSelectedEmployeeOptions(employeeOption ? [employeeOption] : []);
            } else {
                setSelectedEmployeeOptions([]);
            }
            
            // Set department based on grouping or original
            setSelectedDepartmentCode(departmentCode);
            
            // Set attendance tags
            setAttendanceFormData(copiedSchedule.attendanceFormData || {});
            
            // Set free flow fields if needed
            if (isFreeFlow && allowCustomShift) {
                const startTimeFormatted = startTime.length >= 5 ? startTime.slice(0, 5) : startTime.padStart(5, '0');
                const endTimeFormatted = endTime.length >= 5 ? endTime.slice(0, 5) : endTime.padStart(5, '0');
                setFreeFlowStartDateTime(`${dateStr}T${startTimeFormatted}`);
                setFreeFlowEndDateTime(`${dateStr}T${endTimeFormatted}`);
                setFreeFlowShiftName(originalSchedule.shift?.shift_name || "");
            } else {
                setFreeFlowStartDateTime("");
                setFreeFlowEndDateTime("");
                setFreeFlowShiftName("");
            }
            
            // Open modal
            setSelectedDateForShift(date);
            setIsAddShiftModalOpen(true);
            setIsEditMode(false);
            setEditingSchedule(null);
            setCopiedSchedule(null);
            localStorage.removeItem("scheduling_tool_copied_schedule");

            if (pasteModeToastIdRef.current) {
                toast.dismiss(pasteModeToastIdRef.current);
                pasteModeToastIdRef.current = null;
            }
            
            toast.success(`Schedule pasted for ${targetEmployeeName || 'selected employee'}. Review and click Save.`);
            return;
        }

        console.log("Cell clicked:", date);
        setSelectedDateForShift(date);
        setIsAddShiftModalOpen(true);
        setIsEditMode(false);
        setEditingSchedule(null);

        // Initialize form with date
        const dateStr = format(date, "yyyy-MM-dd");
        const defaultDateTime = format(date, "yyyy-MM-dd'T'HH:mm");
        const defaultEmployeeOptionList =
            groupingType === "employee" && group?.group_id
                ? (() => {
                      const option = getEmployeeOptionById(group.group_id, group.group_name);
                      return option ? [option] : [];
                  })()
                : [];
        setSelectedEmployeeOptions(defaultEmployeeOptionList);
        
        // Auto-select department when in department view
        const departmentCode = groupingType === "department" && group?.group_id ? String(group.group_id) : null;
        setSelectedDepartmentCode(departmentCode);

        // Auto-select site when in site view
        let siteId: number | undefined;
        if (groupingType === "site" && group?.group_id) {
            // group_id is the site_code, need to look up site_id from sitesV2
            const siteCode = group.group_id;
            if (sitesV2 && sitesV2.length > 0) {
                const site = sitesV2.find(s => s.us_site_code === siteCode);
                if (site && site.us_user_site_profile_id) {
                    siteId = parseInt(site.us_user_site_profile_id.toString());
                } else {
                    // Fallback: try to parse as integer (for backward compatibility)
                    const parsed = parseInt(siteCode);
                    if (!isNaN(parsed)) {
                        siteId = parsed;
                    }
                }
            } else {
                // Fallback: try to parse as integer (for backward compatibility)
                const parsed = parseInt(siteCode);
                if (!isNaN(parsed)) {
                    siteId = parsed;
                }
            }
        }

        const defaultEmployeeIds =
            defaultEmployeeOptionList.length > 0
                ? defaultEmployeeOptionList
                      .map((opt) => Number(opt.value))
                      .filter((id) => !Number.isNaN(id))
                : [];

        // Load saved color preference from shift_planner_settings
        const savedColor = getSchedulingToolShiftColor();
        
        setFormData({
            singleuser: 1,
            occurence_type: "DAY",
            add_days: 1,
            start_from_custom: 0,
            schedule_date: dateStr,
            os_emp_start_datetime: `${dateStr} 10:00 AM`,
            os_emp_end_datetime: `${dateStr} 05:00 PM`,
            start_from_date: "",
            attendancetags: undefined,
            os_emp_account_id: defaultEmployeeIds as number[],
            shift_color: savedColor,
            department_code: departmentCode || undefined,
            us_user_site_profile_id: siteId,
        });

        // Initialize free flow fields
        setOnlyNotes(false);
        setFreeFlowStartDateTime(allowCustomShift ? defaultDateTime : "");
        setFreeFlowEndDateTime(allowCustomShift ? defaultDateTime : "");
        setFreeFlowShiftName("");

        // Pre-populate attendance tags when creating from a tag value row
        // group_id format is: {tag_field_code}_{value_code} (e.g., "attschdr2_w1")
        if (groupingType === 'tags' && group?.group_id) {
            const groupId = group.group_id;
            
            console.log('Pre-populating tag from group:', {
                groupId,
                selectedTagCode,
                attendanceTagsCount: attendanceTags?.length,
                attendanceTagsCodes: attendanceTags?.map((t: any) => t.field_code)
            });
            
            // Extract tag field code and value code
            // group_id can be:
            // 1. "{tag_field_code}_{value_code}" (e.g., "attschdr2_w1") - when no selectedTagCode
            // 2. "{value_code}" (e.g., "w1") - when selectedTagCode is set
            let tagFieldCode: string | null = null;
            let valueCode: string | null = null;
            
            if (selectedTagCode) {
                // If selectedTagCode is set, check if group_id starts with it (format: "attschdr2_w1")
                if (groupId.startsWith(`${selectedTagCode}_`)) {
                    tagFieldCode = selectedTagCode;
                    valueCode = groupId.substring(selectedTagCode.length + 1);
                } else {
                    // Otherwise, group_id is just the value code (e.g., "w1" or "w2")
                    tagFieldCode = selectedTagCode;
                    valueCode = groupId;
                }
            } else {
                // Extract tag field code from group_id by finding the last underscore
                // This handles cases where tag field code might contain underscores
                const lastUnderscoreIndex = groupId.lastIndexOf('_');
                if (lastUnderscoreIndex > 0) {
                    tagFieldCode = groupId.substring(0, lastUnderscoreIndex);
                    valueCode = groupId.substring(lastUnderscoreIndex + 1);
                }
            }
            
            console.log('Extracted tag info:', { tagFieldCode, valueCode });
            
            if (tagFieldCode && valueCode) {
                // Find the tag definition to get the value_text
                const tagDefinition = attendanceTags?.find((t: any) => t.field_code === tagFieldCode);
                
                console.log('Tag definition found:', {
                    found: !!tagDefinition,
                    tagFieldCode,
                    tagDefinition: tagDefinition ? {
                        field_code: tagDefinition.field_code,
                        field_name: tagDefinition.field_name,
                        field_json_length: Array.isArray(tagDefinition.field_json) ? tagDefinition.field_json.length : 0
                    } : null
                });
                
                if (tagDefinition) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const rawFieldJson: any = tagDefinition.field_json;
                    const fieldJsonArray = rawFieldJson 
                        ? (Array.isArray(rawFieldJson) ? rawFieldJson : [rawFieldJson])
                        : [];
                    
                    console.log('Field JSON array:', {
                        length: fieldJsonArray.length,
                        valueCodes: fieldJsonArray.map((v: any) => v.value_code || v.field_value_code),
                        searchingFor: valueCode
                    });
                    
                    // Find the value definition - use case-insensitive matching
                    const valueDefinition = fieldJsonArray.find((v: any) => {
                        const vCode = (v.value_code || v.field_value_code || '').toLowerCase();
                        return vCode === valueCode?.toLowerCase();
                    });
                    
                    if (valueDefinition) {
                        const valueText = valueDefinition.value_text || valueDefinition.field_value_text || valueCode;
                        
                        console.log('Pre-populating attendance tag from row:', {
                            tagFieldCode,
                            valueCode,
                            valueText,
                            tagDefinition: tagDefinition.field_name
                        });
                        
                        // Pre-populate the attendance form data
                        setAttendanceFormData({
                            [tagFieldCode]: [{
                                value_code: valueCode,
                                value_text: valueText
                            }]
                        });
                        
                        // Automatically show the optional fields section so user can see the pre-populated tag
                        setShowOptionalFields(true);
                    } else {
                        console.warn('Value definition not found:', {
                            valueCode,
                            availableValues: fieldJsonArray.map((v: any) => ({
                                value_code: v.value_code || v.field_value_code,
                                value_text: v.value_text || v.field_value_text
                            }))
                        });
                        // Reset attendance form data if value not found
                        setAttendanceFormData({});
                    }
                } else {
                    console.warn('Tag definition not found:', {
                        tagFieldCode,
                        availableTags: attendanceTags?.map((t: any) => t.field_code)
                    });
                    // Reset attendance form data if tag definition not found
                    setAttendanceFormData({});
                }
            } else {
                console.warn('Could not extract tag field code and value code from group_id:', groupId);
                // Reset attendance form data if group_id doesn't match the tag pattern
                setAttendanceFormData({});
            }
        } else {
            // Reset attendance form data for non-tag views
            setAttendanceFormData({});
        }

        console.log("Modal should be open now");
    };

    // Handle schedule click to edit existing schedule
    const handleScheduleClick = (schedule: ScheduleV3Schedule, date: Date, group?: ScheduleV3Group) => {
        // Allow opening modal to view details even without edit privileges
        // Edit/delete buttons will be disabled if user lacks privileges or date is in past (checked in modal render)
        
        console.log("=== Schedule clicked ===");
        console.log("Full schedule object:", schedule);
        console.log("Schedule ID (os_emp_schedules_id):", (schedule as any).os_emp_schedules_id);
        console.log("Employee Account ID:", schedule.employee?.id);
        console.log("Schedule tags:", schedule.tags);
        console.log("Group context:", group);
        console.log("Current grouping type:", groupingType);

        // If we have a schedule id, fetch detailed ScheduleV2 data (for swap/release actionables)
        // once per id. Backend decides whether action buttons are allowed.
        const rawScheduleId =
            (schedule as any).os_emp_schedules_id != null
                ? (schedule as any).os_emp_schedules_id
                : null;
        if (rawScheduleId != null) {
            const scheduleIdStr = String(rawScheduleId);
            if (lastScheduleDetailId !== scheduleIdStr) {
                setLastScheduleDetailId(scheduleIdStr);
                dispatch(getMyScheduleDetails({ schedule_id: scheduleIdStr }));
            }
        }
        setSelectedDateForShift(date);
        setEditingSchedule(schedule);
        setIsEditMode(true);
        setIsAddShiftModalOpen(true);

        console.log("Modal opened in EDIT mode - isEditMode should be true");
        console.log("======================");

        // Pre-fill form with existing schedule data
        const dateStr = format(date, "yyyy-MM-dd");

        // Extract site, department, and job code
        // The V3 API doesn't return these in the schedule object directly
        // We need to infer from the group context based on grouping type
        let siteId: number | undefined;
        let departmentCode: string | undefined;
        let jobCodeId: number | undefined;

        // Infer from group context based on current grouping type
        if (group) {
            console.log("Group ID:", group.group_id, "Group Name:", group.group_name);

            // When grouped by department, group_id is the department code
            if (groupingType === "department") {
                departmentCode = group.group_id;
                console.log("Inferred department from group:", departmentCode);
            }
            // When grouped by site, group_id is the site_code (not site_id)
            else if (groupingType === "site") {
                // group_id is now site_code, need to look up site_id from sitesV2
                const siteCode = group.group_id;
                if (sitesV2 && sitesV2.length > 0) {
                    const site = sitesV2.find(s => 
                        s.us_site_code === siteCode
                    );
                    if (site && site.us_user_site_profile_id) {
                        siteId = parseInt(site.us_user_site_profile_id.toString());
                        console.log("Inferred site_id from site_code:", { siteCode, siteId });
                    } else {
                        // Fallback: try to parse as integer (for backward compatibility)
                        const parsed = parseInt(siteCode);
                        if (!isNaN(parsed)) {
                            siteId = parsed;
                            console.log("Fallback: parsed site_code as integer:", siteId);
                        }
                    }
                } else {
                    // Fallback: try to parse as integer (for backward compatibility)
                    const parsed = parseInt(siteCode);
                    if (!isNaN(parsed)) {
                        siteId = parsed;
                        console.log("Fallback: parsed site_code as integer (no sitesV2):", siteId);
                    }
                }
            }
            // When grouped by job_code, group_id is the job code ID
            else if (groupingType === "job_code") {
                jobCodeId = parseInt(group.group_id);
                console.log("Inferred job code from group:", jobCodeId);
            }
        }

        console.log("Extracted values - Site:", siteId, "Department:", departmentCode, "Job Code:", jobCodeId);
        
        // Try to get site and work code from schedule object if available
        // Check if schedule has these fields (may vary by API version)
        const scheduleAny = schedule as any;
        
        // Priority order for finding site_id:
        // 1. schedule.site.site_id (new format) - try to match with us_user_site_profile_id
        // 2. schedule.site.site_code matched with sitesV2.us_site_code
        // 3. schedule.site.us_user_site_profile_id (legacy format)
        // 4. schedule.us_user_site_profile_id (direct property)
        // 5. schedule.site_code matched with sitesV2
        let actualSiteId = siteId;
        
        if (schedule.site && sitesV2) {
            // Check if site_id is provided and not null
            if (schedule.site.site_id !== null && schedule.site.site_id !== undefined) {
                // Try to find site by matching site_id with us_user_site_profile_id
                const siteIdNum = typeof schedule.site.site_id === 'string' 
                    ? parseInt(schedule.site.site_id, 10) 
                    : schedule.site.site_id;
                const foundById = sitesV2.find(s => 
                    s.us_user_site_profile_id !== null && 
                    s.us_user_site_profile_id !== undefined &&
                    s.us_user_site_profile_id === siteIdNum
                );
                if (foundById && foundById.us_user_site_profile_id) {
                    actualSiteId = foundById.us_user_site_profile_id;
                }
            }
            
            // If not found by site_id, try by site_code
            if ((!actualSiteId || actualSiteId === siteId) && schedule.site.site_code) {
                const foundByCode = sitesV2.find(s => 
                    s.us_site_code === schedule.site?.site_code
                );
                if (foundByCode && foundByCode.us_user_site_profile_id) {
                    actualSiteId = foundByCode.us_user_site_profile_id;
                }
            }
        }
        
        // Fallback to other methods if still not found
        if (!actualSiteId || actualSiteId === siteId) {
            actualSiteId = scheduleAny.site?.us_user_site_profile_id || 
                          scheduleAny.us_user_site_profile_id || 
                          (scheduleAny.site?.site_code && sitesV2?.find(s => s.us_site_code === scheduleAny.site.site_code)?.us_user_site_profile_id) ||
                          (scheduleAny.site_code && sitesV2?.find(s => s.us_site_code === scheduleAny.site_code)?.us_user_site_profile_id) ||
                          siteId;
        }
        // Extract workcode ID - check multiple possible field names and locations
        let actualWorkCodeId: number | undefined = undefined;
        if (scheduleAny.workcode?.os_work_codes_id) {
            actualWorkCodeId = typeof scheduleAny.workcode.os_work_codes_id === 'number' 
                ? scheduleAny.workcode.os_work_codes_id 
                : parseInt(String(scheduleAny.workcode.os_work_codes_id));
        } else if (scheduleAny.workcode?.work_codes_id) {
            actualWorkCodeId = parseInt(String(scheduleAny.workcode.work_codes_id));
        } else if (scheduleAny.os_work_codes_id) {
            actualWorkCodeId = typeof scheduleAny.os_work_codes_id === 'number' 
                ? scheduleAny.os_work_codes_id 
                : parseInt(String(scheduleAny.os_work_codes_id));
        } else if (scheduleAny.workcode?.work_code && jobCodes) {
            const found = jobCodes.find(j => j.work_code_number === scheduleAny.workcode.work_code);
            actualWorkCodeId = found?.os_work_codes_id;
        } else if (scheduleAny.work_code && jobCodes) {
            const found = jobCodes.find(j => j.work_code_number === scheduleAny.work_code);
            actualWorkCodeId = found?.os_work_codes_id;
        } else {
            actualWorkCodeId = jobCodeId;
        }
        const actualDepartmentCode = scheduleAny.department?.department_code ||
                                    scheduleAny.department_code ||
                                    departmentCode;

        // Determine if this is a free flow shift (no shift template selected)
        // If shift_id exists, assume it's a template shift and pre-populate it
        // Only treat as free flow if shift_id doesn't exist or is explicitly marked as free flow
        const scheduleShiftId = schedule.shift?.shift_id;
        const scheduleShiftCode = schedule.shift?.shift_code;
        
        // Check if this is explicitly a free flow shift (no shift_code or empty shift_code)
        // But if shift_id exists, we should still try to pre-populate the shift dropdown
        const isExplicitFreeFlow = !scheduleShiftCode || scheduleShiftCode === '';
        
        // If shift_id exists, try to use it as template shift
        // We'll pre-populate the shift_id and let the user see it in the dropdown
        const hasShiftId = !!scheduleShiftId;
        
        // Convert shift_id to the format expected by the form (number)
        // The select dropdown uses os_schedules_site_shifts_id which should match shift_id
        let shiftIdForForm: number | undefined = undefined;
        if (hasShiftId) {
            const shiftIdNum = typeof scheduleShiftId === 'number' 
                ? scheduleShiftId 
                : parseInt(String(scheduleShiftId));
            // Verify the shift exists in our shifts list (if loaded)
            // If shifts aren't loaded yet, still set it and let it show when shifts load
            if (!shifts || shifts.length === 0 || shifts.some(s => 
                String(s.os_schedules_site_shifts_id) === String(shiftIdNum)
            )) {
                shiftIdForForm = shiftIdNum;
                console.log("Pre-populating shift ID:", shiftIdForForm);
            } else {
                console.log("Shift ID not found in shifts list:", shiftIdNum, "Available shifts:", shifts.map(s => s.os_schedules_site_shifts_id));
            }
        }
        
        // Set free flow datetime if it's explicitly free flow AND no shift_id to match
        let freeFlowStart = '';
        let freeFlowEnd = '';
        if (isExplicitFreeFlow && !shiftIdForForm && schedule.shift?.start_time && schedule.shift?.end_time) {
            // Use shift_start_date and shift_end_date if available (API), otherwise fall back to clicked date
            const shiftDates = schedule.shift as any;
            const startDate = shiftDates?.shift_start_date || dateStr;
            const endDate = shiftDates?.shift_end_date || dateStr;
            
            // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
            // Ensure time is in HH:mm format (pad if needed)
            const startTime = schedule.shift.start_time.length >= 5 
                ? schedule.shift.start_time.slice(0, 5) 
                : schedule.shift.start_time.padStart(5, '0');
            const endTime = schedule.shift.end_time.length >= 5 
                ? schedule.shift.end_time.slice(0, 5) 
                : schedule.shift.end_time.padStart(5, '0');
            
            freeFlowStart = `${startDate}T${startTime}`;
            freeFlowEnd = `${endDate}T${endTime}`;
            setFreeFlowStartDateTime(freeFlowStart);
            setFreeFlowEndDateTime(freeFlowEnd);
            
            // Pre-populate shift name from existing schedule
            setFreeFlowShiftName(schedule.shift?.shift_name || "");
        }

        // Use shift_start_date and shift_end_date if available, otherwise fall back to clicked date
        const shiftDates = schedule.shift as any;
        const scheduleStartDate = shiftDates?.shift_start_date || dateStr;
        const scheduleEndDate = shiftDates?.shift_end_date || dateStr;
        
        // Load saved color preference from shift_planner_settings if schedule doesn't have one
        const savedColor = getSchedulingToolShiftColor();
        
        setFormData({
            singleuser: 1,
            occurence_type: "DAY",
            add_days: 1,
            start_from_custom: 0,
            schedule_date: scheduleStartDate, // Use actual start date from schedule
            os_emp_account_id: schedule.employee?.id ? [parseInt(schedule.employee.id)] : [],
            os_schedules_site_shifts_id: shiftIdForForm,
            os_emp_start_datetime: schedule.shift?.start_time ? `${scheduleStartDate} ${schedule.shift.start_time}` : undefined,
            os_emp_end_datetime: schedule.shift?.end_time ? `${scheduleEndDate} ${schedule.shift.end_time}` : undefined,
            title: schedule.shift?.shift_name,
            shift_color: schedule.shift?.shift_color || savedColor,
            logs: (schedule.notes === null || schedule.notes === 'null' || schedule.notes === undefined) ? '' : schedule.notes,
            us_user_site_profile_id: actualSiteId,
            department_code: actualDepartmentCode,
            os_work_codes_id: actualWorkCodeId,
            start_from_date: "",
            attendancetags: undefined,
        });

        setSelectedEmployeeOptions([]);
        setSelectedDepartmentCode(actualDepartmentCode || null);

        // Pre-fill attendance tags if available
        if (schedule.tags && schedule.tags.length > 0) {
            const tagsData: Record<string, { value_code: string; value_text: string }[]> = {};
            schedule.tags.forEach((tag: any) => {
                // Handle both old format { label, value } and new format { field_code, field_values }
                if (tag.field_code && tag.field_values && Array.isArray(tag.field_values)) {
                    // New format: { field_code: string, field_values: [{ value_code, value_text }] }
                    tagsData[tag.field_code] = tag.field_values;
                } else if (typeof tag.label === "string") {
                    // Old format: { label: field_code, value: field_value }
                    // Convert to new format
                    tagsData[tag.label] = [
                        {
                            value_code: typeof tag.value === "object" ? tag.value.value_code : tag.label,
                            value_text: typeof tag.value === "object" ? tag.value.value_text : String(tag.value),
                        },
                    ];
                }
            });
            setAttendanceFormData(tagsData);
        }

        console.log("Edit modal should be open now with form data:", formData);
    };

    const formatTime12 = React.useCallback((time24: string) => {
        const [hours, minutes] = time24.split(":");
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    }, []);

    // Template shifts that end at or before their start time should display as cross-day so the modal matches stored data.
    const getTemplateShiftDateTimes = React.useCallback(
        (
            baseDate: Date,
            shiftStartTime: string,
            shiftEndTime: string,
            occurenceType: string = "DAY",
            addDaysValue: number = 1
        ) => {
            const normalizedAddDays = addDaysValue > 0 ? addDaysValue : 1;
            const repeatSpanDays =
                (occurenceType === "WEEK" ? normalizedAddDays * 7 : normalizedAddDays) - 1;
            const startDate = new Date(baseDate);
            const startDateStr = format(startDate, "yyyy-MM-dd");
            const startTime = shiftStartTime.substring(0, 5);
            const endTime = shiftEndTime.substring(0, 5);
            const startMinutes =
                parseInt(startTime.substring(0, 2), 10) * 60 + parseInt(startTime.substring(3, 5), 10);
            const endMinutes =
                parseInt(endTime.substring(0, 2), 10) * 60 + parseInt(endTime.substring(3, 5), 10);

            let endDate = addDays(startDate, repeatSpanDays);
            if (endMinutes <= startMinutes) {
                endDate = addDays(endDate, 1);
            }

            const endDateStr = format(endDate, "yyyy-MM-dd");

            return {
                startDateTimeLocal: `${startDateStr}T${startTime}`,
                endDateTimeLocal: `${endDateStr}T${endTime}`,
                startDateTimeDisplay: `${startDateStr} ${formatTime12(shiftStartTime)}`,
                endDateTimeDisplay: `${endDateStr} ${formatTime12(shiftEndTime)}`,
            };
        },
        [formatTime12]
    );

    // Handle form input changes (memoized to prevent re-renders)
    const handleInputChange = React.useCallback(
        (field: keyof AddScheduleRequest, value: any) => {
            setFormData((prev) => {
                const updatedFormData = { ...prev, [field]: value };

                // Save color preference to shift_planner_settings when it changes
                if (field === "shift_color" && value) {
                    setSchedulingToolShiftColor(value);
                }

                if (field === "department_code") {
                    setSelectedDepartmentCode(value || null);
                }

                // If shift is selected and add_days or occurence_type changed, update end datetime
                if (
                    (field === "add_days" || field === "occurence_type") &&
                    prev.os_schedules_site_shifts_id &&
                    selectedDateForShift
                ) {
                    const selectedShift = shifts?.find(
                        (s) => s.os_schedules_site_shifts_id === String(prev.os_schedules_site_shifts_id)
                    );
                    if (selectedShift?.os_shift_start_time && selectedShift.os_shift_end_time) {
                        // Use updated value for calculation (ensure valid number)
                        const addDaysValue =
                            field === "add_days"
                                ? value && !isNaN(value) && value > 0
                                    ? value
                                    : 1
                                : prev.add_days && prev.add_days > 0
                                ? prev.add_days
                                : 1;
                        const occurenceTypeValue = field === "occurence_type" ? value : prev.occurence_type || "DAY";
                        const templateDateTimes = getTemplateShiftDateTimes(
                            selectedDateForShift,
                            selectedShift.os_shift_start_time,
                            selectedShift.os_shift_end_time,
                            occurenceTypeValue,
                            addDaysValue
                        );

                        setFreeFlowStartDateTime(templateDateTimes.startDateTimeLocal);
                        setFreeFlowEndDateTime(templateDateTimes.endDateTimeLocal);
                        updatedFormData.os_emp_start_datetime = templateDateTimes.startDateTimeDisplay;
                        updatedFormData.os_emp_end_datetime = templateDateTimes.endDateTimeDisplay;
                    }
                }

                return updatedFormData;
            });
        },
        [getTemplateShiftDateTimes, selectedDateForShift, shifts]
    );

    // Handle shift selection and auto-populate times (memoized to prevent re-renders)
    const handleShiftChange = React.useCallback(
        (shiftId: number) => {
            const selectedShift = shifts?.find((s) => s.os_schedules_site_shifts_id === String(shiftId));

            if (selectedShift?.os_shift_start_time && selectedShift.os_shift_end_time && selectedDateForShift) {
                // Use functional update to get current formData values
                setFormData((prev) => {
                    const templateDateTimes = getTemplateShiftDateTimes(
                        selectedDateForShift,
                        selectedShift.os_shift_start_time,
                        selectedShift.os_shift_end_time,
                        prev.occurence_type || "DAY",
                        prev.add_days || 1
                    );

                    setFreeFlowStartDateTime(templateDateTimes.startDateTimeLocal);
                    setFreeFlowEndDateTime(templateDateTimes.endDateTimeLocal);

                    return {
                        ...prev,
                        os_schedules_site_shifts_id: shiftId,
                        os_emp_start_datetime: templateDateTimes.startDateTimeDisplay,
                        os_emp_end_datetime: templateDateTimes.endDateTimeDisplay,
                        title: selectedShift.os_shift_name, // Auto-populate title too
                        shift_code: selectedShift.os_shift_code,
                    };
                });

                // Clear onlyNotes when shift is selected
                setOnlyNotes(false);
            } else if (!shiftId && selectedDateForShift) {
                // Shift cleared - initialize free flow fields when custom shifts are allowed
                setFormData((prev) => ({
                    ...prev,
                    os_schedules_site_shifts_id: undefined,
                }));

                const defaultDateTime = format(selectedDateForShift, "yyyy-MM-dd'T'HH:mm");
                setFreeFlowStartDateTime(allowCustomShift ? defaultDateTime : "");
                setFreeFlowEndDateTime(allowCustomShift ? defaultDateTime : "");
                setFreeFlowShiftName("");
            } else {
                setFormData((prev) => ({
                    ...prev,
                    os_schedules_site_shifts_id: shiftId,
                }));
            }
        },
        [allowCustomShift, getTemplateShiftDateTimes, selectedDateForShift, shifts]
    );

    // State for attendance tags form data
    const [attendanceFormData, setAttendanceFormData] = useState<
        Record<string, { value_code: string; value_text: string }[]>
    >({});

    // Handle attendance field changes (memoized to prevent re-renders)
    const handleAttendanceFieldChange = React.useCallback((fieldCode: string, value: any) => {
        setAttendanceFormData((prev) => ({
            ...prev,
            [fieldCode]: value,
        }));
    }, []);

    // Build tags payload in the format expected by the API (same as AddTimesheetEntry.tsx)
    function buildTagsPayload(
        localSelectedMap: Record<string, { value_code: string; value_text: string }[]>,
        attendanceTagsOptions: any[]
    ): { field_code: string; field_values: { value_code: string; value_text: string }[] }[] {
        // First identify root tags (those without parents or with null parent)
        const rootFields = attendanceTagsOptions.filter(
            (tag) => tag.parent_labor_dimension_id === null || tag.parent_labor_dimension_id === ""
        );

        const rootFieldCodes = rootFields.map((field) => field.field_code);

        // Filter out any local selections that aren't in our map or don't have values
        const validSelections = Object.entries(localSelectedMap).filter(([_, values]) => values && values.length > 0);

        // Split into root selections and child selections
        const rootSelections = validSelections.filter(([field_code]) => rootFieldCodes.includes(field_code));

        // Start with root tags
        const result: { field_code: string; field_values: { value_code: string; value_text: string }[] }[] =
            rootSelections.map(([field_code, values]) => ({
                field_code,
                field_values: values.map((v) => ({
                    value_code: v.value_code,
                    value_text: v.value_text,
                })),
            }));

        // If no root selections, return empty array
        if (result.length === 0) {
            return [];
        }

        // Store all non-root selections, organized by their level in the hierarchy
        const findFieldLevelAndPath = (fieldCode: string): { level: number; path: string[] } => {
            // Find the field by its code
            const field = attendanceTagsOptions.find((t) => t.field_code === fieldCode);
            if (!field) return { level: 0, path: [] };

            // If it's a root field, return level 1
            if (!field.parent_labor_dimension_id) {
                return { level: 1, path: [field.field_code] };
            }

            // Otherwise, find its parent and add 1 to the parent's level
            let current = field;
            const path = [fieldCode];
            let level = 1;

            // Traverse up the hierarchy until we reach a root field
            while (current.parent_labor_dimension_id) {
                const parentField = attendanceTagsOptions.find(
                    (t) => String(t.labor_dimensions_id) === current.parent_labor_dimension_id
                );

                if (!parentField) break;

                path.unshift(parentField.field_code);
                current = parentField;
                level++;
            }

            return { level, path };
        };

        // Group non-root selections by their hierarchy level and path
        const nonRootSelections = validSelections
            .filter(([field_code]) => !rootFieldCodes.includes(field_code))
            .map(([field_code, values]) => {
                const { level, path } = findFieldLevelAndPath(field_code);
                return {
                    field_code,
                    values,
                    level,
                    path,
                };
            })
            // Sort by level to process in order from higher levels to lower levels
            .sort((a, b) => a.level - b.level);

        // Add non-root selections as separate items in the result array
        // instead of nesting them under their parent tags
        if (nonRootSelections.length > 0) {
            nonRootSelections.forEach(({ field_code, values }) => {
                // Find the tag info for this field
                const tagInfo = attendanceTagsOptions.find((t) => t.field_code === field_code);

                if (tagInfo) {
                    // Add the child selection as a separate entry in the result
                    result.push({
                        field_code,
                        field_values: values.map((value) => ({
                            value_code: value.value_code,
                            value_text: value.value_text,
                        })),
                    });
                }
            });
        }

        return result;
    }

    // Function to validate required attendance tags
    const validateRequiredAttendanceTags = () => {
        if (!attendanceTags || attendanceTags.length === 0) {
            return { isValid: true, missingFields: [] };
        }

        const missingFields: string[] = [];

        attendanceTags.forEach((tag) => {
            if (tag.required === "true") {
                const fieldValue = attendanceFormData[tag.field_code];

                // Check if field is empty or has no valid selection
                // fieldValue should be an array of { value_code, value_text } objects
                if (
                    !fieldValue ||
                    !Array.isArray(fieldValue) ||
                    fieldValue.length === 0 ||
                    !fieldValue[0]?.value_code ||
                    !fieldValue[0]?.value_text
                ) {
                    missingFields.push(tag.field_name);
                }
            }
        });

        return {
            isValid: missingFields.length === 0,
            missingFields,
        };
    };

    // Handle form submission
    const handleSubmitSchedule = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to create or edit schedules");
            return;
        }

        // For edit mode, we need to validate either shift selection OR free flow datetime
        // For add mode, we need to validate employee selection
        if (isEditMode) {
            // Edit mode validations - allow either shift template OR free flow
            const hasShift = !!formData.os_schedules_site_shifts_id;
            const hasFreeFlow = allowCustomShift && !onlyNotes && freeFlowStartDateTime && freeFlowEndDateTime;
            const hasOnlyNotes = onlyNotes && freeFlowStartDateTime;
            
            if (!hasShift && !hasFreeFlow && !hasOnlyNotes) {
                toast.error(allowCustomShift ? "Please select a shift template or enter free flow date/time." : "Please select a shift template.");
                return;
            }

            // Validate required attendance tags (skip if only notes mode)
            if (!onlyNotes) {
                const validation = validateRequiredAttendanceTags();
                if (!validation.isValid) {
                    toast.error(`Please fill in required attendance fields: ${validation.missingFields.join(", ")}`);
                    return;
                }
            }

            // Get the schedule ID from editingSchedule
            // Priority: os_emp_schedules_id > shift.shift_id > other fallbacks
            console.log("editingSchedule", JSON.stringify(editingSchedule, null, 2));
            const scheduleId =
                editingSchedule?.os_emp_schedules_id ||
                editingSchedule?.shift?.shift_id ||
                (editingSchedule as any)?.schedules_id ||
                (editingSchedule as any)?.id;

            if (!scheduleId) {
                toast.error("Cannot update: Missing schedule ID.");
                console.error("Could not find schedule ID in:", editingSchedule);
                return;
            }

            // Get employee ID - should be a single employee in edit mode
            const employeeId = formData.os_emp_account_id?.[0] || editingSchedule?.employee?.id;

            if (!employeeId) {
                toast.error("Cannot update: Missing employee ID.");
                return;
            }

            setIsSubmitting(true);
            try {
                let result;

                if (USE_UPDATE_SCHEDULE_V2) {
                    // Use V2 API
                    // Build tags payload
                    const tagsPayload = buildTagsPayload(attendanceFormData, attendanceTags || []);
                    console.log("formdata", formData);
                    
                    // Build site_code and work_code
                    const siteCode = formData.us_user_site_profile_id
                        ? sitesV2?.find((s) => s.us_user_site_profile_id === formData.us_user_site_profile_id)?.us_site_code
                        : undefined;
                    const workCode = formData.os_work_codes_id
                        ? jobCodes?.find((j) => j.os_work_codes_id === formData.os_work_codes_id)?.work_code_number
                        : undefined;
                    
                    // Build attendance_tags array
                    // For multi-select tags, group values into an array
                    const attendanceTagsArray = tagsPayload.length > 0
                        ? tagsPayload.map((tag) => {
                              // If no values, send empty string
                              if (tag.field_values.length === 0) {
                                  return {
                                      code: tag.field_code,
                                      value: "",
                                  };
                              }
                              // If single value, send as string
                              if (tag.field_values.length === 1) {
                                  return {
                                      code: tag.field_code,
                                      value: tag.field_values[0].value_text || tag.field_values[0].value_code || "",
                                  };
                              }
                              // If multiple values, send as array
                              return {
                                  code: tag.field_code,
                                  value: tag.field_values.map((fieldValue) => 
                                      fieldValue.value_text || fieldValue.value_code || ""
                                  ),
                              };
                          })
                        : undefined;

                    // If only notes mode, only send notes
                    if (onlyNotes) {
                        const v2UpdateRequest: UpdateScheduleV2Request = {
                            schedule_id: parseInt(String(scheduleId)),
                            os_emp_account_id: String(employeeId),
                            shift_type: "notes" as const,
                            shift_attributes: {
                                logs: formData.logs || undefined,
                            },
                        };

                        result = await dispatch(updateScheduleV2(v2UpdateRequest));
                    } else {
                        // Determine if updating to free flow or template shift
                        const isUpdatingToFreeFlow = allowCustomShift && !formData.os_schedules_site_shifts_id;
                        let freeFlowShiftData: { start_datetime: string; end_datetime: string; title?: string } | undefined;
                        
                        if (isUpdatingToFreeFlow) {
                            freeFlowShiftData = {
                                start_datetime: toMysqlDateTime(freeFlowStartDateTime),
                                end_datetime: toMysqlDateTime(freeFlowEndDateTime),
                                title: freeFlowShiftName || undefined,
                            };
                        }
                        
                        // Build request based on shift type
                        const v2UpdateRequest: UpdateScheduleV2Request = {
                            schedule_id: parseInt(String(scheduleId)),
                            os_emp_account_id: String(employeeId),
                            shift_type: (isUpdatingToFreeFlow ? "free_flow" : "template") as "free_flow" | "template",
                            schedule_date: formData.schedule_date,
                            shift_attributes: {
                                logs: formData.logs || undefined,
                                shift_color: formData.shift_color || undefined,
                                department_code: formData.department_code || undefined,
                                site_code: siteCode,
                                work_code: workCode,
                                attendance_tags: attendanceTagsArray,
                            },
                        };

                        // Add shift-specific fields
                        if (isUpdatingToFreeFlow) {
                            v2UpdateRequest.free_flow_shift = freeFlowShiftData;
                        } else {
                            v2UpdateRequest.os_schedules_site_shifts_id = String(formData.os_schedules_site_shifts_id);
                        }

                        result = await dispatch(updateScheduleV2(v2UpdateRequest));
                    }
                } else {
                    // Use V1 API
                    const tagsPayload = buildTagsPayload(attendanceFormData, attendanceTags || []);

                    // If only notes mode, only send notes
                    let updateRequest: any;
                    if (onlyNotes) {
                        updateRequest = {
                            os_emp_schedules_id: parseInt(String(scheduleId)),
                            os_emp_account_id: String(employeeId),
                            logs: formData.logs || undefined,
                        };
                    } else {
                        updateRequest = {
                            os_emp_schedules_id: parseInt(String(scheduleId)),
                            os_emp_account_id: String(employeeId),
                            os_schedules_site_shifts_id: formData.os_schedules_site_shifts_id,
                            schedule_date: formData.schedule_date,
                            us_user_site_profile_id: formData.us_user_site_profile_id
                                ? String(formData.us_user_site_profile_id)
                                : undefined,
                            department_code: formData.department_code,
                            os_work_codes_id: formData.os_work_codes_id ? String(formData.os_work_codes_id) : undefined,
                            shift_color: formData.shift_color,
                            logs: formData.logs,
                            attendancetags: tagsPayload,
                        };
                    }

                    result = await dispatch(updateSchedule(updateRequest));
                }

                if (USE_UPDATE_SCHEDULE_V2) {
                    if (updateScheduleV2.fulfilled.match(result)) {
                        setIsAddShiftModalOpen(false);
                        const payload = result.payload as any;
                        const assignedCount = payload?.data?.assigned_count || payload?.assigned_count;
                        const message =
                            payload?.message ||
                            `Schedule updated successfully!${assignedCount ? ` (${assignedCount} updated)` : ""}`;
                        toast.success(message);
                        setAttendanceFormData({});
                        if (onScheduleAdded) {
                            onScheduleAdded();
                        }
                    } else {
                        const errorMessage =
                            typeof result.payload === "string"
                                ? result.payload
                                : (result.payload as any)?.message || "Unknown error";
                        toast.error(cleanErrorMessage(errorMessage));
                    }
                } else {
                    if (updateSchedule.fulfilled.match(result)) {
                        setIsAddShiftModalOpen(false);
                        const message = result.payload?.message || "Schedule updated successfully!";
                        toast.success(message);
                        setAttendanceFormData({});
                        if (onScheduleAdded) {
                            onScheduleAdded();
                        }
                    } else {
                        const errorMessage =
                            typeof result.payload === "string"
                                ? result.payload
                                : (result.payload as any)?.message || "Unknown error";
                        toast.error(cleanErrorMessage(errorMessage));
                    }
                }
            } catch (error: any) {
                console.error("Error updating schedule:", error);
                const errorMessage = error.response?.data?.message || error.message || "Unknown error";
                toast.error(cleanErrorMessage(errorMessage));
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Add mode validations
            if (!formData.os_emp_account_id || formData.os_emp_account_id.length === 0) {
                toast.error(
                    "Please select at least one employee. Employee selection is required to create a schedule."
                );
                return;
            }

            // Validate shift selection OR free flow mode
            const hasShift = !!formData.os_schedules_site_shifts_id;
            const hasFreeFlow = allowCustomShift && !hasShift && freeFlowStartDateTime;

            if (!hasShift && !hasFreeFlow) {
                toast.error(allowCustomShift ? "Please select a shift or provide free flow start date time." : "Please select a shift template.");
                return;
            }

            // If free flow mode, validate end datetime (unless only notes)
            if (hasFreeFlow && !onlyNotes && !freeFlowEndDateTime) {
                toast.error("Please select end date time for free flow shift.");
                return;
            }

            // Validate required attendance tags (skip if only notes mode)
            if (!onlyNotes) {
                const validation = validateRequiredAttendanceTags();
                if (!validation.isValid) {
                    toast.error(`Please fill in required attendance fields: ${validation.missingFields.join(", ")}`);
                    return;
                }
            }

            setIsSubmitting(true);
            try {
                let result;

                if (USE_ADD_SCHEDULE_V2) {
                    // Use V2 API
                    // Build tags payload
                    const tagsPayload = buildTagsPayload(attendanceFormData, attendanceTags || []);

                    // Determine shift type (template or free_flow)
                    const hasShift = !!formData.os_schedules_site_shifts_id;
                    const selectedShift = hasShift
                        ? shifts?.find(
                              (s) => s.os_schedules_site_shifts_id === String(formData.os_schedules_site_shifts_id)
                          )
                        : null;

                    // Determine start_date based on mode
                    let startDate: string;
                    let freeFlowShift: { start_datetime: string; end_datetime: string; title?: string } | undefined;

                    if (hasShift && selectedShift) {
                        // Template mode - use schedule_date
                        startDate = formData.schedule_date || format(selectedDateForShift!, "yyyy-MM-dd");
                    } else {
                        // Free flow mode - extract date from datetime
                        startDate = format(new Date(freeFlowStartDateTime), "yyyy-MM-dd");

                        if (onlyNotes) {
                            // Only Notes mode - use start as both start and end
                            freeFlowShift = {
                                start_datetime: toMysqlDateTime(freeFlowStartDateTime),
                                end_datetime: toMysqlDateTime(freeFlowStartDateTime),
                            };
                        } else {
                            // Normal free flow mode - use both start and end
                            freeFlowShift = {
                                start_datetime: toMysqlDateTime(freeFlowStartDateTime),
                                end_datetime: toMysqlDateTime(freeFlowEndDateTime),
                                title: freeFlowShiftName || undefined,
                            };
                        }
                    }

                    // If only notes mode, only send notes
                    let v2Request: any;
                    if (onlyNotes) {
                        v2Request = {
                            assign_to_employee_ids: (formData.os_emp_account_id || []).map((id) => String(id)),
                            shift_type: "notes", // Notes-only mode
                            repeat_type: (formData.occurence_type || "DAY") as "DAY" | "WEEK",
                            repeat_count: formData.add_days && formData.add_days > 0 ? formData.add_days : 1,
                            start_date: startDate,
                            shift_attributes: {
                                notes: formData.logs || undefined,
                            },
                        };
                    } else {
                        // Convert V1 form data to V2 format
                        v2Request = {
                            assign_to_employee_ids: (formData.os_emp_account_id || []).map((id) => String(id)),
                            shift_type: hasShift ? "template" : "free_flow",
                            repeat_type: (formData.occurence_type || "DAY") as "DAY" | "WEEK",
                            repeat_count: formData.add_days && formData.add_days > 0 ? formData.add_days : 1,
                            start_date: startDate,
                            shift_attributes: {
                                notes: formData.logs,
                                shift_color: formData.shift_color,
                                department_code: formData.department_code,
                                site_code: formData.us_user_site_profile_id
                                    ? sitesV2?.find((s) => s.us_user_site_profile_id === formData.us_user_site_profile_id)
                                          ?.us_site_code
                                    : undefined,
                                work_code: formData.os_work_codes_id
                                    ? jobCodes?.find((j) => j.os_work_codes_id === formData.os_work_codes_id)
                                          ?.work_code_number
                                    : undefined,
                                attendance_tags: tagsPayload.map((tag) => {
                                    // If no values, send empty string
                                    if (tag.field_values.length === 0) {
                                        return {
                                            code: tag.field_code,
                                            value: "",
                                        };
                                    }
                                    // If single value, send as string
                                    if (tag.field_values.length === 1) {
                                        return {
                                            code: tag.field_code,
                                            value: tag.field_values[0].value_text || tag.field_values[0].value_code || "",
                                        };
                                    }
                                    // If multiple values, send as array
                                    return {
                                        code: tag.field_code,
                                        value: tag.field_values.map((fieldValue) => 
                                            fieldValue.value_text || fieldValue.value_code || ""
                                        ),
                                    };
                                }),
                            },
                        };

                        // Add shift_code for template mode or free_flow_shift for free flow mode
                        if (hasShift && selectedShift) {
                            v2Request.shift_code =
                                selectedShift.os_shift_code ||
                                String(formData.shift_code || formData.os_schedules_site_shifts_id);
                        } else if (freeFlowShift) {
                            v2Request.free_flow_shift = freeFlowShift;
                        }
                    }

                    result = await dispatch(addScheduleV2(v2Request));
                } else {
                    // Use V1 API
                    const tagsPayload = buildTagsPayload(attendanceFormData, attendanceTags || []);

                    // If only notes mode, only send notes
                    let schedulePayload: any;
                    if (onlyNotes) {
                        schedulePayload = {
                            os_emp_account_id: formData.os_emp_account_id,
                            schedule_date: formData.schedule_date,
                            occurence_type: formData.occurence_type,
                            add_days: formData.add_days,
                            logs: formData.logs || undefined,
                        };
                    } else {
                        schedulePayload = {
                            ...formData,
                            attendancetags: tagsPayload.length > 0 ? tagsPayload : undefined,
                        };
                    }

                    result = await dispatch(addSchedule(schedulePayload));
                }

                // Check which API was used based on the flag
                if (USE_ADD_SCHEDULE_V2) {
                    if (addScheduleV2.fulfilled.match(result)) {
                        // Success! Close modal and refresh schedules
                        setIsAddShiftModalOpen(false);
                        const payload = result.payload as any;
                        const assignedCount = payload?.data?.assigned_count || payload?.assigned_count;
                        const message =
                            payload?.message ||
                            `Schedule added successfully!${assignedCount ? ` (${assignedCount} assigned)` : ""}`;
                        showScheduleResultToast(payload, message);

                        // Reset form data
                        setAttendanceFormData({});

                        // Call the callback to refresh schedules
                        if (onScheduleAdded) {
                            onScheduleAdded();
                        }
                    } else {
                        // Error
                        const errorMessage =
                            typeof result.payload === "string"
                                ? result.payload
                                : (result.payload as any)?.message || "Unknown error";
                        toast.error(cleanErrorMessage(errorMessage));
                    }
                } else {
                    if (addSchedule.fulfilled.match(result)) {
                        // Success! Close modal and refresh schedules
                        setIsAddShiftModalOpen(false);
                        const payload = result.payload as any;
                        const message = payload?.message || "Schedule added successfully!";
                        showScheduleResultToast(payload, message);

                        // Reset form data
                        setAttendanceFormData({});

                        // Call the callback to refresh schedules
                        if (onScheduleAdded) {
                            onScheduleAdded();
                        }
                    } else {
                        // Error
                        const errorMessage =
                            typeof result.payload === "string"
                                ? result.payload
                                : (result.payload as any)?.message || "Unknown error";
                        toast.error(cleanErrorMessage(errorMessage));
                    }
                }
            } catch (error: any) {
                console.error("Error adding schedule:", error);
                const errorMessage = error.response?.data?.message || error.message || "Unknown error";
                toast.error(cleanErrorMessage(errorMessage));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const canPasteIntoCell = (date: Date) =>
        Boolean(copiedSchedule) &&
        hasScheduleToolPrivilege &&
        SchedulingToolUtils.canEditDate(date, schedulingConfig);

    const tryHandlePasteCellClick = (
        date: Date,
        group?: ScheduleV3Group,
        event?: React.MouseEvent
    ) => {
        if (!canPasteIntoCell(date)) return false;

        event?.preventDefault();
        event?.stopPropagation();
        handleCellClick(date, group, event);
        return true;
    };

    const getCellHoverKey = (date: Date, group?: ScheduleV3Group) =>
        `${format(date, "yyyy-MM-dd")}::${group?.group_id ?? "month"}`;

    // Handle delete button click - show confirmation
    const handleDeleteSchedule = () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to delete schedules");
            return;
        }

        console.log("Delete button clicked!");
        console.log("Editing schedule FULL OBJECT:", JSON.stringify(editingSchedule, null, 2));

        // Check if deletion is allowed for this date
        if (selectedDateForShift && !SchedulingToolUtils.canEditDate(selectedDateForShift, schedulingConfig)) {
            toast.error("Cannot delete shifts for past dates");
            return;
        }

        // Try to find schedule ID in different possible locations
        // Priority: os_emp_schedules_id > shift.shift_id
        const scheduleId =
            editingSchedule?.os_emp_schedules_id ||
            editingSchedule?.shift?.shift_id ||
            (editingSchedule as any)?.schedules_id ||
            (editingSchedule as any)?.id;

        const employeeId =
            editingSchedule?.employee?.id ||
            (editingSchedule as any)?.employee?.emp_id ||
            (editingSchedule as any)?.employee?.employee_id;

        console.log(
            "Found Schedule ID:",
            scheduleId,
            "from field:",
            editingSchedule?.os_emp_schedules_id
                ? "os_emp_schedules_id"
                : editingSchedule?.shift?.shift_id
                ? "shift.shift_id"
                : (editingSchedule as any)?.schedules_id
                ? "schedules_id"
                : (editingSchedule as any)?.id
                ? "id"
                : "NONE"
        );
        console.log(
            "Found Employee ID:",
            employeeId,
            "from field:",
            editingSchedule?.employee?.id
                ? "employee.id"
                : (editingSchedule as any)?.employee?.emp_id
                ? "employee.emp_id"
                : (editingSchedule as any)?.employee?.employee_id
                ? "employee.employee_id"
                : "NONE"
        );

        if (!scheduleId || !employeeId) {
            console.error("Missing required fields for deletion!");
            console.error("Schedule object keys:", Object.keys(editingSchedule || {}));
            console.error("Employee object keys:", Object.keys(editingSchedule?.employee || {}));
            console.error("Shift object keys:", Object.keys(editingSchedule?.shift || {}));
            toast.error(
                `Cannot delete: Missing ${
                    !scheduleId ? "schedule ID" : "employee ID"
                }. Please check the console for the full schedule object.`
            );
            return;
        }

        console.log("Opening delete confirmation modal with IDs:", { scheduleId, employeeId });
        setDeleteConfirmationStep(1);
        setShowDeleteConfirmation(true);
    };

    // Handle actual deletion after confirmation
    const confirmDeleteSchedule = async () => {
        // Try to find schedule ID in different possible locations
        // Priority: os_emp_schedules_id > shift.shift_id
        const scheduleId =
            editingSchedule?.os_emp_schedules_id ||
            editingSchedule?.shift?.shift_id ||
            (editingSchedule as any)?.schedules_id ||
            (editingSchedule as any)?.id;

        const employeeId =
            editingSchedule?.employee?.id ||
            (editingSchedule as any)?.employee?.emp_id ||
            (editingSchedule as any)?.employee?.employee_id;

        if (!scheduleId || !employeeId) {
            console.error("Cannot delete - missing IDs");
            console.error("Schedule ID:", scheduleId, "Employee ID:", employeeId);
            return;
        }

        setIsDeleting(true);
        setShowDeleteConfirmation(false);

        try {
            let result;
            if (USE_DELETE_SCHEDULE_V2) {
                // Use V2 API - only requires schedule_id
                result = await dispatch(deleteScheduleV2(parseInt(String(scheduleId))));
            } else {
                // Use V1 API - requires schedule_id and employee_id
                result = await dispatch(
                    deleteSchedule({
                        os_emp_schedules_id: parseInt(String(scheduleId)),
                        os_emp_account_id: employeeId as any,
                    })
                );
            }

            const isSuccess = USE_DELETE_SCHEDULE_V2
                ? deleteScheduleV2.fulfilled.match(result)
                : deleteSchedule.fulfilled.match(result);

            if (isSuccess) {
                // Success! Close modal and refresh schedules
                setIsAddShiftModalOpen(false);

                // Show success message
                const payload = result.payload as any;
                const message = payload?.message || "Successfully removed.";
                setSuccessMessage(message);
                setShowSuccessMessage(true);

                // Hide success message after 3 seconds
                setTimeout(() => {
                    setShowSuccessMessage(false);
                }, 3000);

                // Reset form data
                setAttendanceFormData({});

                // Call the callback to refresh schedules
                if (onScheduleAdded) {
                    onScheduleAdded();
                }
            }
        } catch (error: any) {
            console.error("Error deleting schedule:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Open assign modal and load published shift (used from detail modal when manager clicks Assign)
    const openAssignModalForOpenShift = async (openShift: ScheduleV3OpenShift, day: Date, group: ScheduleV3Group) => {
        setSelectedOpenShift({
            openShift,
            day,
            group,
            open_shift_id: openShift.open_shift_id,
        });
        setShowAssignOpenShiftModal(true);
        setSelectedEmployeeForOpenShift(null);
        setPublishedShiftForAssign(null);
        setIsLoadingPublishedShift(true);
        try {
            const shiftDate = openShift.shift_date || format(day, "yyyy-MM-dd");
            const result = await dispatch(getPublishedShiftsForAssign({
                start_date: DateTimeUtil.formatDateToMySQL(new Date(shiftDate), true),
                end_date: DateTimeUtil.formatDateToMySQL(new Date(shiftDate), true),
            }));
            if (getPublishedShiftsForAssign.fulfilled.match(result)) {
                const publishedShifts = result.payload.data.published_shifts || [];
                const matchingShift = publishedShifts.find((shift: PublishedShiftForAssign) =>
                    shift.other_details_json?.shift_id === openShift.shift_details?.shift_id &&
                    shift.shift_date === shiftDate
                );
                if (matchingShift) setPublishedShiftForAssign(matchingShift);
            }
        } catch (error: any) {
            console.error("Error fetching published shifts:", error);
        } finally {
            setIsLoadingPublishedShift(false);
        }
    };

    // Handle open shift click – open detail modal first (claim for employees, publish/assign for managers)
    const handleOpenShiftClick = (openShift: ScheduleV3OpenShift, day: Date, group: ScheduleV3Group) => {
        const shiftId =
            (openShift as any).os_sch_open_shift_transactions_id ||
            (openShift as any).open_shift_id ||
            "";
        setOpenShiftDetailModalShiftId(shiftId || null);
        setOpenShiftDetailModalInitial(openShift);
        setOpenShiftDetailModalDay(day);
        setOpenShiftDetailModalGroup(group);
        setOpenShiftDetailModalOpen(true);
    };

    // Load employee options for open shift assignment
    const loadEmployeeOptionsForOpenShift = async (inputValue: string) => {
        if (inputValue.length < 3) {
            // Return default eligible employees if available
            if (publishedShiftForAssign?.eligible_employees) {
                return publishedShiftForAssign.eligible_employees.map(emp => ({
                    value: emp.emp_id,
                    label: emp.emp_name
                }));
            }
            return [];
        }

        try {
            const response = await dispatch(searchEmployee({
                search: inputValue,
                departments: '',
                groups: '',
                site_id: '',
                page: 1,
                module_name: ModuleName.EMPLOYEE_PRIV_SCHEDULE_TOOL
            })).unwrap();

            // Filter by eligible employees if published shift data is available
            if (publishedShiftForAssign?.eligible_emps_json?.EMP_ID) {
                const eligibleEmpIds = publishedShiftForAssign.eligible_emps_json.EMP_ID;
                return response.data.employees
                    .filter((employee: any) => eligibleEmpIds.includes(employee.os_emp_account_id))
                    .map((employee: any) => ({
                        value: employee.os_emp_account_id,
                        label: employee.text
                    }));
            }

            // If no eligible employees list, return all searched employees
            return response.data.employees.map((employee: any) => ({
                value: employee.os_emp_account_id,
                label: employee.text
            }));
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    };

    // Handle assign open shift
    const handleAssignOpenShift = async () => {
        // Check privileges
        if (!hasScheduleToolPrivilege) {
            toast.error("You do not have permission to assign open shifts");
            return;
        }

        if (!selectedOpenShift || !selectedEmployeeForOpenShift) {
            toast.warning('Please select an employee to assign');
            return;
        }

        setIsAssigningOpenShift(true);

        try {
            // Prefer os_sch_open_shift_transactions_id from published shift, fallback to open_shift_id
            const shiftId = publishedShiftForAssign?.os_sch_open_shift_transactions_id 
                || selectedOpenShift.open_shift_id 
                || selectedOpenShift.openShift?.open_shift_id;
            
            if (!shiftId) {
                toast.error('Invalid open shift: missing shift ID');
                return;
            }

            const result = await dispatch(assignOpenShiftToEmployee({
                shifts_data: [{
                    selected_shifts: shiftId,
                    assigned_employees: selectedEmployeeForOpenShift.value
                }]
            }));

            if (assignOpenShiftToEmployee.fulfilled.match(result)) {
                const message = result.payload?.message || 'Open shift assigned successfully!';
                toast.success(message);
                setShowAssignOpenShiftModal(false);
                setSelectedOpenShift(null);
                setSelectedEmployeeForOpenShift(null);
                setPublishedShiftForAssign(null);

                // Refresh the current view after assigning
                if (onScheduleAdded) {
                    onScheduleAdded();
                }
            } else {
                const errorMessage = typeof result.payload === 'string' 
                    ? result.payload 
                    : (result.payload as any)?.message || 'Unknown error';
                toast.error(cleanErrorMessage(errorMessage));
            }
        } catch (error: any) {
            console.error('Error assigning open shift:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(cleanErrorMessage(errorMessage));
        } finally {
            setIsAssigningOpenShift(false);
        }
    };

    // Move to second confirmation step
    const handleFirstConfirmation = () => {
        setDeleteConfirmationStep(2);
    };

    // Cancel delete and close confirmation
    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setDeleteConfirmationStep(1);
    };

    // Add Shift Modal Component - memoized to prevent unnecessary re-renders
    const addShiftModalContent = useMemo(() => {
        if (!isAddShiftModalOpen || !selectedDateForShift) return null;

        // Determine if form should be read-only (past date or user lacks privileges)
        const isReadOnly = (!hasScheduleToolPrivilege) || (isEditMode && !SchedulingToolUtils.canEditDate(selectedDateForShift, schedulingConfig));

        return (
            <AddShiftModal
                open={isAddShiftModalOpen}
                selectedDateForShift={selectedDateForShift}
                schedulingConfig={schedulingConfig}
                isEditMode={isEditMode}
                isReadOnly={isReadOnly}
                hasScheduleToolPrivilege={hasScheduleToolPrivilege}
                editingSchedule={editingSchedule}
                formData={formData as any}
                attendanceFormData={attendanceFormData as any}
                shifts={shifts as any}
                onlyNotes={onlyNotes}
                freeFlowStartDateTime={freeFlowStartDateTime}
                freeFlowEndDateTime={freeFlowEndDateTime}
                freeFlowShiftName={freeFlowShiftName}
                selectedEmployeeOptions={selectedEmployeeOptions}
                showOptionalFields={showOptionalFields}
                isLoadingAttendanceTags={isLoadingAttendanceTags}
                sitesV2={sitesV2 as any}
                departments={departments as any}
                jobCodes={jobCodes as any}
                attendanceTags={attendanceTags as any}
                isSubmitting={isSubmitting}
                isDeleting={isDeleting}
                onCopy={() => {
                    if (!editingSchedule) return;
                    const copyData = {
                        schedule: editingSchedule,
                        formData: { ...formData },
                        attendanceFormData: { ...attendanceFormData },
                    };
                    setCopiedSchedule(copyData);
                    localStorage.setItem(
                        "scheduling_tool_copied_schedule",
                        JSON.stringify(copyData)
                    );
                    setIsAddShiftModalOpen(false);
                    setIsEditMode(false);
                    setEditingSchedule(null);
                    setAttendanceFormData({});
                    setOnlyNotes(false);
                    setFreeFlowStartDateTime("");
                    setFreeFlowEndDateTime("");
                    setFreeFlowShiftName("");
                    if (pasteModeToastIdRef.current) {
                        toast.dismiss(pasteModeToastIdRef.current);
                    }
                    pasteModeToastIdRef.current = toast.success("Schedule copied! Click on any date cell to paste.");
                }}
                onHeaderClose={() => {
                    setIsAddShiftModalOpen(false);
                    setIsEditMode(false);
                    setEditingSchedule(null);
                    setAttendanceFormData({});
                    setOnlyNotes(false);
                    setFreeFlowStartDateTime("");
                    setFreeFlowEndDateTime("");
                    setFreeFlowShiftName("");
                }}
                onCancel={() => {
                    setIsAddShiftModalOpen(false);
                    setOnlyNotes(false);
                    setFreeFlowStartDateTime("");
                    setFreeFlowEndDateTime("");
                    setFreeFlowShiftName("");
                }}
                handleShiftChange={handleShiftChange}
                handleFreeFlowStartChange={handleFreeFlowStartChange}
                handleFreeFlowEndChange={handleFreeFlowEndChange}
                setFreeFlowShiftName={setFreeFlowShiftName}
                setSelectedEmployeeOptions={setSelectedEmployeeOptions}
                loadEmployeeOptionsForSchedule={loadEmployeeOptionsForSchedule}
                handleInputChange={(field: string, value: any) =>
                    handleInputChange(field as any, value)
                }
                setShowOptionalFields={setShowOptionalFields}
                handleAttendanceFieldChange={handleAttendanceFieldChange}
                handleSubmitSchedule={handleSubmitSchedule}
                handleDeleteSchedule={handleDeleteSchedule}
            />
        );
    }, [
        isAddShiftModalOpen,
        selectedDateForShift,
        isEditMode,
        editingSchedule,
        formData,
        attendanceFormData,
        showOptionalFields,
        isLoadingAttendanceTags,
        shifts,
        sitesV2,
        departments,
        jobCodes,
        attendanceTags,
        isSubmitting,
        isDeleting,
        handleShiftChange,
        handleInputChange,
        handleAttendanceFieldChange,
        handleSubmitSchedule,
        handleDeleteSchedule,
        onlyNotes,
        freeFlowStartDateTime,
        freeFlowEndDateTime,
        selectedEmployeeOptions,
        hasScheduleToolPrivilege,
        schedulingConfig,
    ]);

    const handleCloseUnavailabilityModal = () => {
        setIsUnavailabilityModalOpen(false);
        setSelectedUnavailability(null);
    };

    const handleWithdrawUnavailability = async (params: { unavailabilitySlotId: string; date: string; notes?: string }) => {
        const slotId = Number(params.unavailabilitySlotId);
        if (!Number.isInteger(slotId)) return;
        setIsWithdrawingUnavailability(true);
        try {
            const res = await updateUnavailability({
                employee_unavailability_slot_id: slotId,
                date: params.date,
                status: -1,
                notes: params.notes?.trim() ?? "",
            });
            if (res.status) {
                handleCloseUnavailabilityModal();
                onScheduleAdded?.();
                toast.success("Unavailability withdrawn successfully.");
            } else {
                toast.error(cleanErrorMessage(res.message || "Failed to withdraw"));
            }
        } catch (e) {
            toast.error(cleanErrorMessage((e as any)?.message || "Failed to withdraw"));
        } finally {
            setIsWithdrawingUnavailability(false);
        }
    };

    const handleCloseTimeoffDetailModal = () => {
        setIsTimeoffDetailModalOpen(false);
        setSelectedTimeoffForModal(null);
    };

    const handleWithdrawTimeoff = async (params: { schedules_id: string; notes?: string }) => {
        const timeOffRequestId = selectedTimeoffForModal?.time_off_request_id;
        const startDatetime = selectedTimeoffForModal?.timeoff?.start_datetime;
        const request_date =
            startDatetime && startDatetime.length >= 10
                ? startDatetime.slice(0, 10)
                : format(new Date(), "yyyy-MM-dd");
        setIsWithdrawingTimeoff(true);
        try {
            await dispatch(deleteTimesheet({ record_id: params.schedules_id })).unwrap();
            if (timeOffRequestId != null && String(timeOffRequestId).trim() !== "") {
                updateTimeoffRequestLog({
                    request_date,
                    time_off_request_id: String(timeOffRequestId).trim(),
                });
            }
            handleCloseTimeoffDetailModal();
            onScheduleAdded?.();
            toast.success("Time off withdrawn successfully.");
        } catch (e) {
            toast.error(cleanErrorMessage((e as any)?.message || "Failed to withdraw"));
        } finally {
            setIsWithdrawingTimeoff(false);
        }
    };


    // Monthly calendar grid view (like the image)
    if (viewType === "monthly") {
        // Get all weeks in the month
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        // Get the first day of the first week (Monday)
        const firstWeekStart = startOfWeek(startOfMonth, { weekStartsOn: 1 });
        // Get the last day of the last week (Sunday)
        const lastWeekEnd = endOfWeek(endOfMonth, { weekStartsOn: 1 });

        // Calculate number of days
        const totalDays = Math.ceil((lastWeekEnd.getTime() - firstWeekStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const weeks = Math.ceil(totalDays / 7);

        return (
            <>
                {addShiftModalContent}
                <UnavailabilityDetailModal
                    open={isUnavailabilityModalOpen}
                    onClose={handleCloseUnavailabilityModal}
                    data={selectedUnavailability}
                    formatDateString={formatDateString}
                    onWithdraw={handleWithdrawUnavailability}
                    isWithdrawing={isWithdrawingUnavailability}
                    user={user}
                />
                <TimeoffDetailModal
                    open={isTimeoffDetailModalOpen}
                    onClose={handleCloseTimeoffDetailModal}
                    timeOffRequestId={selectedTimeoffForModal?.time_off_request_id ?? null}
                    schedulesId={selectedTimeoffForModal?.timeoff?.schedules_id ?? null}
                    onWithdraw={handleWithdrawTimeoff}
                    isWithdrawing={isWithdrawingTimeoff}
                    user={user}
                />
                <AssignOpenShiftModal
                    open={showAssignOpenShiftModal}
                    selectedOpenShift={selectedOpenShift}
                    schedulingConfig={schedulingConfig}
                    isLoadingPublishedShift={isLoadingPublishedShift}
                    publishedShiftForAssign={publishedShiftForAssign}
                    selectedEmployeeForOpenShift={selectedEmployeeForOpenShift}
                    onClose={() => {
                        setShowAssignOpenShiftModal(false);
                        setSelectedOpenShift(null);
                        setSelectedEmployeeForOpenShift(null);
                        setPublishedShiftForAssign(null);
                    }}
                    onSelectEmployee={setSelectedEmployeeForOpenShift}
                    loadEmployeeOptions={loadEmployeeOptionsForOpenShift}
                    onAssign={handleAssignOpenShift}
                    isAssigning={isAssigningOpenShift}
                />
                <OpenShiftDetailModal
                    open={openShiftDetailModalOpen}
                    onClose={() => {
                        setOpenShiftDetailModalOpen(false);
                        setOpenShiftDetailModalShiftId(null);
                        setOpenShiftDetailModalInitial(null);
                        setOpenShiftDetailModalDay(undefined);
                        setOpenShiftDetailModalGroup(undefined);
                    }}
                    shiftId={openShiftDetailModalShiftId}
                    initialOpenShift={openShiftDetailModalInitial}
                    day={openShiftDetailModalDay}
                    group={openShiftDetailModalGroup}
                    hasScheduleToolPrivilege={hasScheduleToolPrivilege}
                    onAssign={
                        openShiftDetailModalInitial &&
                        openShiftDetailModalDay &&
                        openShiftDetailModalGroup
                            ? (payload) =>
                                  openAssignModalForOpenShift(
                                      payload.openShift,
                                      payload.day,
                                      payload.group
                                  )
                            : undefined
                    }
                    onPublish={onPublishOpenShift}
                    onCancelShiftSuccess={onCancelShiftSuccess}
                />
                <DeleteConfirmationModal
                    open={showDeleteConfirmation}
                    step={deleteConfirmationStep}
                    schedule={editingSchedule}
                    onCancel={cancelDelete}
                    onFirstConfirm={handleFirstConfirmation}
                    onConfirm={confirmDeleteSchedule}
                    isDeleting={isDeleting}
                />
                <SuccessNotification
                    show={showSuccessMessage}
                    message={successMessage}
                    onClose={() => setShowSuccessMessage(false)}
                />
                <ShowMoreModal
                    open={showMoreModal}
                    date={showMoreDate}
                    items={showMoreItems}
                    groupingType={groupingType}
                    onClose={() => setShowMoreModal(false)}
                    onScheduleClick={handleScheduleClick}
                    onTimeoffClick={(item) => {
                        const effectiveRequestId = getEffectiveTimeoffRequestId(item.timeoff);
                        if (effectiveRequestId != null) {
                            setSelectedTimeoffForModal({
                                timeoff: item.timeoff,
                                groupName: item.groupName,
                                time_off_request_id: effectiveRequestId,
                            });
                            setIsTimeoffDetailModalOpen(true);
                        }
                    }}
                    onUnavailabilityClick={(item) => {
                        const unavail = item.unavailability;
                        const rawStart = (unavail as any)?.start_time ?? (unavail as any)?.from_time ?? null;
                        const rawEnd = (unavail as any)?.end_time ?? (unavail as any)?.to_time ?? null;
                        const allDayValue = (unavail as any).all_day;
                        const isFullDay =
                            unavail.all_day === true ||
                            allDayValue === "1" ||
                            allDayValue === "true" ||
                            allDayValue === 1 ||
                            (!rawStart && !rawEnd);
                        const prettyTimeRange = formatUnavailabilityTimeRange(rawStart, rawEnd);
                        const unavailType = isFullDay ? "Full Day" : "Partial";
                        const employeeName =
                            (typeof unavail.employee?.name === "string" && unavail.employee.name) ||
                            (typeof (unavail as any).employee_name === "string" && (unavail as any).employee_name) ||
                            "";
                        setSelectedUnavailability({
                            unavail,
                            employeeName,
                            timeRange: prettyTimeRange,
                            isFullDay,
                            unavailType,
                        });
                        setIsUnavailabilityModalOpen(true);
                    }}
                    onOpenShiftClick={
                        showMoreDate
                            ? (item) => {
                                  const openShift = item.openShift as ScheduleV3OpenShift;
                                  const shiftId =
                                      (openShift as any).os_sch_open_shift_transactions_id ||
                                      (openShift as any).open_shift_id ||
                                      "";
                                  setOpenShiftDetailModalShiftId(shiftId || null);
                                  setOpenShiftDetailModalInitial(openShift);
                                  setOpenShiftDetailModalDay(showMoreDate);
                                  setOpenShiftDetailModalGroup(item.group);
                                  setOpenShiftDetailModalOpen(true);
                                  setShowMoreModal(false);
                              }
                            : undefined
                    }
                />

                {/* Copied Schedule Indicator */}
                {copiedSchedule && (
                    <div className="fixed top-4 right-4 bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-3 z-[1300] max-w-sm">
                        <div className="flex items-start gap-3">
                            <Clipboard className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                            <div className="flex-1">
                                <div className="text-sm font-medium text-blue-900">
                                    Schedule Copied
                                </div>
                                <div className="text-xs text-blue-700 mt-1">
                                    {copiedSchedule.schedule.employee?.name} - {copiedSchedule.schedule.shift?.shift_name}
                                </div>
                                <div className="text-xs text-blue-600 mt-2">
                                    Click on any date cell to paste
                                </div>
                                <div className="text-xs text-gray-500 mt-1 italic">
                                    Press ESC or click Done to stop pasting
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <button
                                    onClick={() => {
                                        setCopiedSchedule(null);
                                        localStorage.removeItem("scheduling_tool_copied_schedule");
                                        if (pasteModeToastIdRef.current) {
                                            toast.dismiss(pasteModeToastIdRef.current);
                                            pasteModeToastIdRef.current = null;
                                        }
                                        toast.info("Paste mode cleared");
                                    }}
                                    className="text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0"
                                    title="Clear copied schedule"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-blue-200">
                            <button
                                onClick={() => {
                                    setCopiedSchedule(null);
                                    localStorage.removeItem("scheduling_tool_copied_schedule");
                                    toast.success("Done pasting. Paste mode cleared.");
                                }}
                                className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                            >
                                Done Pasting
                            </button>
                        </div>
                    </div>
                )}
                
                <div className="flex-1 flex flex-col overflow-hidden bg-white">
                    {/* Day Headers */}
                    <div className="relative z-0 grid grid-cols-7 bg-gray-50 border-b-2 border-gray-300">
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, idx) => (
                            <div
                                key={day}
                                className={`p-1.5 md:p-3 text-center font-semibold text-gray-700 text-[10px] md:text-sm ${
                                    idx !== 6 ? "border-r border-gray-300" : ""
                                }`}
                            >
                                <span className="hidden md:inline">{day}</span>
                                <span className="md:hidden">{day.substring(0, 1)}</span>
                            </div>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="relative z-10 flex-1 overflow-y-auto">
                        <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(80px, 1fr)" }}>
                            {Array.from({ length: weeks * 7 }, (_, i) => {
                                const day = addDays(firstWeekStart, i);
                                const dateStr = format(day, "yyyy-MM-dd");
                                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                                const isToday = isSameDay(day, new Date());
                                const columnIndex = i % 7;

                                // Collect all schedules for this day from all groups
                                const allSchedules: Array<{
                                    schedule: ScheduleV3Schedule;
                                    groupName: string;
                                    group: ScheduleV3Group;
                                }> = [];
                                const allTimeoffs: Array<{
                                    timeoff: ScheduleV3Timeoff;
                                    groupName: string;
                                    group: ScheduleV3Group;
                                }> = [];
                                const allOpenShifts: Array<{
                                    openShift: any;
                                    groupName: string;
                                    group: ScheduleV3Group;
                                }> = [];
                                const allUnavailability: Array<{
                                    unavailability: ScheduleV3Unavailability;
                                    groupName: string;
                                    group: ScheduleV3Group;
                                }> = [];
                                groups.forEach((group) => {
                                    const dateData = group.dates[dateStr];
                                    if (dateData?.schedules) {
                                        dateData.schedules.forEach((schedule) => {
                                            allSchedules.push({ schedule, groupName: group.group_name, group });
                                        });
                                    }
                                    if (dateData?.timeoff) {
                                        dateData.timeoff.forEach((timeoff) => {
                                            allTimeoffs.push({ timeoff, groupName: group.group_name, group });
                                        });
                                    }
                                    if (dateData?.unavailability) {
                                        dateData.unavailability.forEach((unavail) => {
                                            allUnavailability.push({ unavailability: unavail, groupName: group.group_name, group });
                                        });
                                    }
                                    if (dateData?.open_shifts) {
                                        dateData.open_shifts.forEach((openShift) => {
                                            allOpenShifts.push({ openShift, groupName: group.group_name, group });
                                        });
                                    }
                                });

                                // Sort schedules by start time
                                allSchedules.sort((a, b) => {
                                    const timeA = a.schedule.shift?.start_time || "";
                                    const timeB = b.schedule.shift?.start_time || "";
                                    return timeA.localeCompare(timeB);
                                });

                                const cellKey = getCellHoverKey(day);
                                const isHovered = hoveredCell === cellKey;
                                const isEmpty = allSchedules.length === 0 && allTimeoffs.length === 0 && allUnavailability.length === 0 && allOpenShifts.length === 0;
                                const showPasteHover = copiedSchedule && isHovered && canPasteIntoCell(day);
                                const shouldOpenTooltipsBelow = true;
                                const monthlyAddGroup = getMonthlyDepartmentGroup();

                                // Calculate visible items
                                const totalItems = allSchedules.length + allTimeoffs.length + allUnavailability.length + allOpenShifts.length;
                                const hasMoreItems = totalItems > MAX_VISIBLE_ITEMS_MONTHLY;
                                const remainingCount = totalItems - MAX_VISIBLE_ITEMS_MONTHLY;

                                // Slice arrays to show limited items
                                const visibleSchedules = hasMoreItems
                                    ? allSchedules.slice(0, Math.min(MAX_VISIBLE_ITEMS_MONTHLY, allSchedules.length))
                                    : allSchedules;
                                const remainingSlots = hasMoreItems
                                    ? MAX_VISIBLE_ITEMS_MONTHLY - visibleSchedules.length
                                    : 0;
                                const visibleTimeoffs = hasMoreItems
                                    ? allTimeoffs.slice(0, Math.max(0, remainingSlots))
                                    : allTimeoffs;
                                
                                // Calculate remaining slots after schedules and timeoffs for unavailability
                                const remainingSlotsAfterTimeoffs = hasMoreItems
                                    ? Math.max(0, MAX_VISIBLE_ITEMS_MONTHLY - visibleSchedules.length - visibleTimeoffs.length)
                                    : Infinity;
                                const visibleUnavailability = hasMoreItems
                                    ? allUnavailability.slice(0, remainingSlotsAfterTimeoffs)
                                    : allUnavailability;
                                
                                // Calculate remaining slots after schedules, timeoffs, and unavailability for open_shifts
                                const remainingSlotsAfterUnavailability = hasMoreItems
                                    ? Math.max(0, MAX_VISIBLE_ITEMS_MONTHLY - visibleSchedules.length - visibleTimeoffs.length - visibleUnavailability.length)
                                    : Infinity;
                                const visibleOpenShifts = hasMoreItems
                                    ? allOpenShifts.slice(0, remainingSlotsAfterUnavailability)
                                    : allOpenShifts;

                                return (
                                    <div
                                        key={i}
                                        className={`p-1 md:p-2 border-b border-gray-200 relative transition-colors overflow-visible ${
                                            columnIndex !== 6 ? "border-r border-gray-200" : ""
                                        } ${!isCurrentMonth ? "bg-gray-50" : "bg-white"} ${
                                            isToday ? "bg-blue-50" : ""
                                        } ${
                                            isEmpty &&
                                            isHovered &&
                                            SchedulingToolUtils.canEditDate(day, schedulingConfig) &&
                                            hasScheduleToolPrivilege
                                                ? "bg-blue-50"
                                                : ""
                                        } ${
                                            SchedulingToolUtils.canEditDate(day, schedulingConfig)
                                                ? "cursor-pointer"
                                                : "cursor-not-allowed"
                                        }`}
                                        onMouseEnter={() => {
                                            // Only allow hover effects if editing is allowed and user has privileges
                                            if (SchedulingToolUtils.canEditDate(day, schedulingConfig) && hasScheduleToolPrivilege) {
                                                setHoveredCell(cellKey);
                                            }
                                        }}
                                        onMouseLeave={() => setHoveredCell(null)}
                                        onClick={(e) => {
                                            if (tryHandlePasteCellClick(day, monthlyAddGroup, e)) {
                                                return;
                                            }

                                            if (isEmpty && SchedulingToolUtils.canEditDate(day, schedulingConfig) && hasScheduleToolPrivilege) {
                                                handleCellClick(day, monthlyAddGroup, e);
                                            }
                                        }}
                                    >
                                        {/* Day Number */}
                                        <div
                                            className={`text-[10px] md:text-sm font-medium mb-0.5 md:mb-1 ${
                                                isToday
                                                    ? "text-blue-600 font-bold"
                                                    : !isCurrentMonth
                                                    ? "text-gray-400"
                                                    : "text-gray-700"
                                            }`}
                                        >
                                            {format(day, "d")}
                                        </div>

                                        {showPasteHover && (
                                            <div className="pointer-events-none absolute inset-1 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-blue-400 bg-blue-100/80">
                                                <span className="rounded-full bg-blue-600 px-2 py-1 text-[10px] font-medium text-white shadow-sm md:text-xs">
                                                    Paste here
                                                </span>
                                            </div>
                                        )}

                                        {/* Schedule Blocks */}
                                        <div className="space-y-0.5">
                                            {visibleSchedules.map((item, idx) => {
                                                const { schedule, groupName, group } = item;
                                                const scheduleColor = schedule.shift?.shift_color || "#4CAF50";
                                                const scheduleConflicts = schedule.os_emp_schedules_id != null
                                                    ? (conflictsByScheduleId.get(schedule.os_emp_schedules_id) ?? []) : [];
                                                const conflictSeverity = getConflictSeverity(scheduleConflicts);

                                                // Get time range
                                                const timeRange = resolveShiftTimeRange(schedule.shift?.start_time, schedule.shift?.end_time);
                                                const displayTimeRange = timeRange && timeRange !== 'N/A' ? timeRange : '';
                                                
                                                // Format card content based on config
                                                const cardFields = displayConfig.schedule.card;
                                                const cardContent = formatCardContent(cardFields, { schedule, timeRange: displayTimeRange }, 'schedule', groupName);
                                                
                                                // Format hover content
                                                const hoverContent = formatHoverContent(displayConfig.schedule.hover, { schedule, timeRange: displayTimeRange }, 'schedule', groupName);
                                                const tooltipSuppressionKey = schedule.os_emp_schedules_id != null
                                                    ? `monthly-schedule-${schedule.os_emp_schedules_id}`
                                                    : `monthly-schedule-${format(day, "yyyy-MM-dd")}-${idx}`;
                                                
                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`text-white rounded px-1 md:px-1.5 py-0.5 md:py-1 text-[8px] md:text-xs cursor-pointer hover:opacity-90 transition-opacity relative z-10 hover:z-30 focus-within:z-30 group overflow-visible ${SchedulingToolUtils.getPublishStatusInfo(schedule)?.isUnpublished ? 'border-2 border-dashed border-white/70' : ''} ${conflictSeverity ? conflictSeverity.ring : ''}`}
                                                        style={{ backgroundColor: scheduleColor }}
                                                        onClick={(e) => {
                                                            if (tryHandlePasteCellClick(day, group, e)) {
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            handleScheduleClick(schedule, day, group);
                                                        }}
                                                        onMouseLeave={() => {
                                                            setSuppressedShiftTooltipKey((current) =>
                                                                current === tooltipSuppressionKey ? null : current
                                                            );
                                                        }}
                                                    >
                                                        <div className="flex items-start justify-between gap-1">
                                                            <div className="flex-1 min-w-0">
                                                                {cardFields.map((field, i) => {
                                                                    const content = getFieldValue(field, { schedule, timeRange, groupName }, 'schedule');
                                                                    if (!content) return null; // Skip empty fields
                                                                    const isEmployeeName = field === 'employeeName';
                                                                    return (
                                                                        <div
                                                                            key={field}
                                                                            className={`truncate ${
                                                                                i === 0 ? 'font-medium text-[8px] md:text-xs' :
                                                                                i === 1 ? 'text-[7px] md:text-[10px]' :
                                                                                'text-[7px] md:text-[10px] opacity-90'
                                                                            } ${isEmployeeName ? 'hidden md:block' : ''}`}
                                                                        >
                                                                            {content}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {/* Mobile fallback for employee name */}
                                                                {cardFields.includes('employeeName') && (
                                                                    <div className="truncate text-[7px] md:hidden">
                                                                        {schedule.employee?.name?.split(" ")[0] || ''}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div
                                                                className="flex items-center gap-0.5 flex-shrink-0"
                                                                onMouseEnter={() => setSuppressedShiftTooltipKey(tooltipSuppressionKey)}
                                                                onMouseLeave={() => {
                                                                    setSuppressedShiftTooltipKey((current) =>
                                                                        current === tooltipSuppressionKey ? null : current
                                                                    );
                                                                }}
                                                            >
                                                            {/* Conflict Badge */}
                                                            {conflictSeverity && scheduleConflicts.length > 0 && (
                                                                <div
                                                                    className="flex-shrink-0 relative"
                                                                    onClick={(e) => {
                                                                        if (tryHandlePasteCellClick(day, group, e)) return;
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    <div
                                                                        className={`inline-flex items-center relative z-10 group/conflict rounded-full p-0.5 group-hover/conflict:z-40 ${conflictSeverity.badge}`}
                                                                    >
                                                                        <AlertTriangle size={12} className="w-3 h-3 md:w-4 md:h-4" />
                                                                        <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow, "center")} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover/conflict:opacity-100 pointer-events-none z-40 transition-opacity min-w-max max-w-[180px]`}>
                                                                            {scheduleConflicts.map((c, i) => (
                                                                                <div key={i} className="whitespace-normal">{c.message}</div>
                                                                            ))}
                                                                            <div className={getTooltipArrowClasses(shouldOpenTooltipsBelow)}>
                                                                                <div className={getTooltipArrowInnerClasses(shouldOpenTooltipsBelow)}></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* Publish Status Icon */}
                                                            {(() => {
                                                                const publishInfo = SchedulingToolUtils.getPublishStatusInfo(schedule);
                                                                if (!publishInfo) return null;

                                                                const IconComponent = publishInfo.iconName === 'XCircle' ? XCircle :
                                                                                       publishInfo.iconName === 'CheckCircle2' ? CheckCircle2 : Clock;

                                                                return (
                                                                    <div
                                                                        className="flex-shrink-0 relative"
                                                                        onClick={(e) => {
                                                                            if (tryHandlePasteCellClick(day, group, e)) {
                                                                                return;
                                                                            }
                                                                            e.stopPropagation();
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className={`inline-flex items-center ${publishInfo.iconColor} relative z-10 group/icon group-hover/icon:z-40 ${publishInfo.isUnpublished ? 'bg-orange-500 rounded-full p-0.5' : ''}`}
                                                                        >
                                                                            <IconComponent size={12} className="w-3 h-3 md:w-4 md:h-4" />
                                                                            {/* Tooltip on hover - only shows when hovering over the icon */}
                                                                            <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow, "center")} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover/icon:opacity-100 pointer-events-none whitespace-nowrap z-40 transition-opacity`}>
                                                                                {publishInfo.tooltip}
                                                                                <div className={getTooltipArrowClasses(shouldOpenTooltipsBelow)}>
                                                                                    <div className={getTooltipArrowInnerClasses(shouldOpenTooltipsBelow)}></div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                            </div>
                                                        </div>
                                                        {/* Hover tooltip - Desktop only */}
                                                        {hoverContent && suppressedShiftTooltipKey !== tooltipSuppressionKey && (
                                                            <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 group-hover/conflict:opacity-0 group-hover/icon:opacity-0 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                                <div className="whitespace-normal">{hoverContent}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Timeoff Blocks */}
                                            {visibleTimeoffs.map((item, idx) => {
                                                const { timeoff, groupName } = item;
                                                const effectiveRequestId = getEffectiveTimeoffRequestId(timeoff);
                                                const hasRequestId = effectiveRequestId != null;
                                                const shouldShowTime = timeoff.show_time === true;
                                                const startTimeRaw = DateTimeUtil.getTime24HourFormat(timeoff.start_datetime);
                                                const endTimeRaw = DateTimeUtil.getTime24HourFormat(timeoff.end_datetime);
                                                const startTime = startTimeRaw === "Invalid time" ? "" : startTimeRaw;
                                                const endTime = endTimeRaw === "Invalid time" ? "" : endTimeRaw;
                                                const timeRange = shouldShowTime && startTime && endTime ? `${startTime} - ${endTime}` : "";
                                                // Check status - handle both string and null/undefined cases
                                                const statusValue = timeoff.status;
                                                const isPending = statusValue && String(statusValue).toUpperCase().trim() === "PENDING";

                                                return (
                                                    <div
                                                        key={`timeoff-${idx}`}
                                                        className={`bg-orange-500 text-white rounded px-1 md:px-1.5 py-0.5 md:py-1 text-[8px] md:text-xs transition-colors relative z-10 hover:z-30 focus-within:z-30 overflow-visible group ${isPending ? 'pending-stripe' : ''} ${hasRequestId ? 'cursor-pointer hover:bg-orange-600' : ''}`}
                                                        title={`${timeoff.employee?.name} - ${timeoff.earnings_code_name}${shouldShowTime && timeRange ? ` - ${timeRange}` : ""} - ${timeoff.hours}h`}
                                                        onClick={hasRequestId ? (e) => {
                                                            if (tryHandlePasteCellClick(day, item.group, e)) {
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            setSelectedTimeoffForModal({
                                                                timeoff,
                                                                groupName,
                                                                time_off_request_id: effectiveRequestId,
                                                            });
                                                            setIsTimeoffDetailModalOpen(true);
                                                        } : undefined}
                                                    >
                                                        <div className="font-medium truncate">
                                                            {timeoff.employee?.name}
                                                        </div>
                                                        <div className="truncate text-[7px] md:text-[10px] opacity-90">
                                                            {timeoff.earnings_code_name}
                                                            {shouldShowTime && timeRange ? ` ${timeRange}` : ""}
                                                            {timeoff.hours && timeoff.hours > 0 ? ` (${timeoff.hours}h)` : ""}
                                                        </div>
                                                        {/* Hover tooltip - Desktop only */}
                                                        <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                            <div className="whitespace-normal">{`${timeoff.employee?.name} - ${timeoff.earnings_code_name}${shouldShowTime && timeRange ? ` - ${timeRange}` : ""} - ${timeoff.hours}h`}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Unavailability Blocks */}
                                            {visibleUnavailability.map((item, idx) => {
                                                const { unavailability, groupName } = item;
                                                const rawStart = (unavailability as any)?.start_time ?? (unavailability as any)?.from_time ?? null;
                                                const rawEnd = (unavailability as any)?.end_time ?? (unavailability as any)?.to_time ?? null;
                                                const allDayValue = (unavailability as any).all_day;
                                                const isFullDay = unavailability.all_day === true || 
                                                                 allDayValue === "1" || 
                                                                 allDayValue === "true" ||
                                                                 allDayValue === 1 ||
                                                                 (!rawStart && !rawEnd);
                                                
                                                // Pretty print time range for display
                                                const prettyTimeRange = formatUnavailabilityTimeRange(rawStart, rawEnd);
                                                
                                                const unavailType = isFullDay ? "Full Day" : "Partial";
                                                const displayText = isFullDay ? unavailType : prettyTimeRange;
                                                
                                                const employeeName = (typeof unavailability.employee?.name === "string" && unavailability.employee.name) ||
                                                                    (typeof (unavailability as any).employee_name === "string" && (unavailability as any).employee_name) ||
                                                                    "";
                                                // Check status - handle both string and null/undefined cases
                                                const unavailStatus = (unavailability as any).status;
                                                const isPending = unavailStatus && String(unavailStatus).toUpperCase().trim() === "PENDING";

                                                return (
                                                    <div
                                                        key={`unavail-${idx}`}
                                                        className={`bg-gray-400 text-white rounded px-1 md:px-1.5 py-0.5 md:py-1 text-[8px] md:text-xs cursor-pointer hover:bg-gray-500 transition-colors relative z-10 hover:z-30 focus-within:z-30 overflow-visible group ${isPending ? 'pending-stripe' : ''}`}
                                                        title={`Unavailable — ${unavailType} | ${prettyTimeRange}${isPending ? ' | Pending Approval' : ''} (${groupName})`}
                                                    onClick={(e) => {
                                                        if (tryHandlePasteCellClick(day, item.group, e)) {
                                                            return;
                                                        }
                                                        e.stopPropagation();
                                                        setSelectedUnavailability({
                                                            unavail: unavailability,
                                                            employeeName,
                                                            timeRange: prettyTimeRange,
                                                            isFullDay,
                                                            unavailType
                                                        });
                                                        setIsUnavailabilityModalOpen(true);
                                                    }}
                                                    >
                                                        <div className="font-medium truncate">
                                                            {displayText}
                                                        </div>
                                                        {employeeName && (
                                                            <div className="truncate text-[7px] md:text-[10px] hidden md:block">
                                                                {employeeName}
                                                            </div>
                                                        )}
                                                        {employeeName && (
                                                            <div className="truncate text-[7px] md:hidden">
                                                                {employeeName.split(" ")[0]}
                                                            </div>
                                                        )}
                                                        {/* Hover tooltip - Desktop only */}
                                                        <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                            <div className="whitespace-normal">{`Unavailable — ${unavailType} | ${prettyTimeRange}${isPending ? ' | Pending Approval' : ''}${groupName ? ` (${groupName})` : ''}`}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Open Shifts Blocks */}
                                            {visibleOpenShifts.map((item, idx) => {
                                                const { openShift, groupName, group } = item;
                                                const startTime = openShift.shift_details?.shift_start_time?.substring(0, 5) || "";
                                                const endTime = openShift.shift_details?.shift_end_time?.substring(0, 5) || "";
                                                const timeRange = startTime && endTime ? `${startTime}-${endTime}` : "";
                                                const openShiftStatusInfo = SchedulingToolUtils.getOpenShiftStatusInfo(openShift as any);
                                                const hoverContent = formatHoverContent(
                                                    displayConfig.openShift.hover,
                                                    { openShift, timeRange },
                                                    'openShift',
                                                    groupName
                                                );

                                                return (
                                                    <div
                                                        key={`open-${idx}`}
                                                        className="border-2 border-dashed border-purple-400 bg-purple-50 text-purple-700 rounded px-1 md:px-1.5 py-0.5 md:py-1 text-[8px] md:text-xs cursor-pointer hover:bg-purple-100 transition-colors relative z-10 hover:z-30 focus-within:z-30 group overflow-visible"
                                                        title={`Open Shift - ${openShift.shift_details?.shift_name || 'Shift'} (${groupName})`}
                                                        onClick={(e) => {
                                                            if (tryHandlePasteCellClick(day, group, e)) {
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            handleOpenShiftClick(openShift, day, group);
                                                        }}
                                                    >
                                                        <div className="font-medium truncate">
                                                            {timeRange || "Open Shift"}
                                                        </div>
                                                        <div className="truncate text-[7px] md:text-[10px] hidden md:flex md:items-center md:gap-1">
                                                            <span>{openShift.shift_details?.shift_name || 'Open Shift'}</span>
                                                            {openShiftStatusInfo && (
                                                                <span
                                                                    className={`inline-flex items-center ${openShiftStatusInfo.isDraft ? "bg-amber-100 rounded-full p-0.5" : ""} ${openShiftStatusInfo.iconColor}`}
                                                                    title={openShiftStatusInfo.tooltip}
                                                                >
                                                                    {openShiftStatusInfo.iconName === "Pencil" ? (
                                                                        <Pencil size={10} />
                                                                    ) : (
                                                                        <CheckCircle2 size={10} />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="truncate text-[7px] md:hidden">
                                                            {(openShift.shift_details?.shift_name || 'Open').split(" ")[0]}
                                                        </div>
                                                        {/* Hover tooltip - Desktop only */}
                                                        <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                            <div className="whitespace-normal">{hoverContent || "Open Shift"}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Show More Button */}
                                            {hasMoreItems && (
                                                <button
                                                    onClick={(e) => {
                                                        if (tryHandlePasteCellClick(day, monthlyAddGroup, e)) {
                                                            return;
                                                        }
                                                        e.stopPropagation();
                                                        setShowMoreItems({
                                                            schedules: allSchedules,
                                                            timeoffs: allTimeoffs,
                                                            unavailability: allUnavailability,
                                                            openShifts: allOpenShifts,
                                                        });
                                                        setShowMoreDate(day);
                                                        setShowMoreModal(true);
                                                    }}
                                                    className="w-full px-1 md:px-2 py-0.5 md:py-1.5 text-[8px] md:text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                                                >
                                                    <span className="hidden md:inline">+ {remainingCount} more</span>
                                                    <span className="md:hidden">+{remainingCount}</span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Add Icon on Hover for Empty Cells */}
                                        {isEmpty && isHovered && hasScheduleToolPrivilege && !copiedSchedule && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-blue-100 bg-opacity-50">
                                                <div className="bg-blue-500 text-white rounded-full p-1.5 md:p-2">
                                                    <Plus size={16} className="md:w-6 md:h-6" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Add Icon for Cells with Schedules - Small + icon in top-right */}
                                        {!isEmpty && SchedulingToolUtils.canEditDate(day, schedulingConfig) && hasScheduleToolPrivilege && (
                                            <button
                                                onClick={(e) => {
                                                    if (tryHandlePasteCellClick(day, monthlyAddGroup, e)) {
                                                        return;
                                                    }
                                                    e.stopPropagation();
                                                    handleCellClick(day, monthlyAddGroup, e);
                                                }}
                                                className="absolute top-1 right-1 p-0.5 md:p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors z-10 flex items-center justify-center"
                                                title="Add Schedule"
                                            >
                                                <Plus size={10} className="md:w-4 md:h-4" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Daily and Weekly grid view
    return (
        <>
            {addShiftModalContent}
            <UnavailabilityDetailModal
                    open={isUnavailabilityModalOpen}
                    onClose={handleCloseUnavailabilityModal}
                    data={selectedUnavailability}
                    formatDateString={formatDateString}
                    onWithdraw={handleWithdrawUnavailability}
                    isWithdrawing={isWithdrawingUnavailability}
                    user={user}
                />
            <TimeoffDetailModal
                open={isTimeoffDetailModalOpen}
                onClose={handleCloseTimeoffDetailModal}
                timeOffRequestId={selectedTimeoffForModal?.time_off_request_id ?? null}
                schedulesId={selectedTimeoffForModal?.timeoff?.schedules_id ?? null}
                onWithdraw={handleWithdrawTimeoff}
                isWithdrawing={isWithdrawingTimeoff}
                user={user}
            />
            <AssignOpenShiftModal
                open={showAssignOpenShiftModal}
                selectedOpenShift={selectedOpenShift}
                schedulingConfig={schedulingConfig}
                isLoadingPublishedShift={isLoadingPublishedShift}
                publishedShiftForAssign={publishedShiftForAssign}
                selectedEmployeeForOpenShift={selectedEmployeeForOpenShift}
                onClose={() => {
                    setShowAssignOpenShiftModal(false);
                    setSelectedOpenShift(null);
                    setSelectedEmployeeForOpenShift(null);
                    setPublishedShiftForAssign(null);
                }}
                onSelectEmployee={setSelectedEmployeeForOpenShift}
                loadEmployeeOptions={loadEmployeeOptionsForOpenShift}
                onAssign={handleAssignOpenShift}
                isAssigning={isAssigningOpenShift}
            />
            <OpenShiftDetailModal
                open={openShiftDetailModalOpen}
                onClose={() => {
                    setOpenShiftDetailModalOpen(false);
                    setOpenShiftDetailModalShiftId(null);
                    setOpenShiftDetailModalInitial(null);
                    setOpenShiftDetailModalDay(undefined);
                    setOpenShiftDetailModalGroup(undefined);
                }}
                shiftId={openShiftDetailModalShiftId}
                initialOpenShift={openShiftDetailModalInitial}
                day={openShiftDetailModalDay}
                group={openShiftDetailModalGroup}
                hasScheduleToolPrivilege={hasScheduleToolPrivilege}
                onAssign={
                    openShiftDetailModalInitial &&
                    openShiftDetailModalDay &&
                    openShiftDetailModalGroup
                        ? (payload) =>
                              openAssignModalForOpenShift(
                                  payload.openShift,
                                  payload.day,
                                  payload.group
                              )
                        : undefined
                }
                onPublish={onPublishOpenShift}
            />
            <DeleteConfirmationModal
                open={showDeleteConfirmation}
                step={deleteConfirmationStep}
                schedule={editingSchedule}
                onCancel={cancelDelete}
                onFirstConfirm={handleFirstConfirmation}
                onConfirm={confirmDeleteSchedule}
                isDeleting={isDeleting}
            />
            <SuccessNotification
                show={showSuccessMessage}
                message={successMessage}
                onClose={() => setShowSuccessMessage(false)}
            />
            <ShowMoreModal
                open={showMoreModal}
                date={showMoreDate}
                items={showMoreItems}
                groupingType={groupingType}
                onClose={() => setShowMoreModal(false)}
                onScheduleClick={handleScheduleClick}
                onTimeoffClick={(item) => {
                    const effectiveRequestId = getEffectiveTimeoffRequestId(item.timeoff);
                    if (effectiveRequestId != null) {
                        setSelectedTimeoffForModal({
                            timeoff: item.timeoff,
                            groupName: item.groupName,
                            time_off_request_id: effectiveRequestId,
                        });
                        setIsTimeoffDetailModalOpen(true);
                    }
                }}
                onUnavailabilityClick={(item) => {
                    const unavail = item.unavailability;
                    const rawStart = (unavail as any)?.start_time ?? (unavail as any)?.from_time ?? null;
                    const rawEnd = (unavail as any)?.end_time ?? (unavail as any)?.to_time ?? null;
                    const allDayValue = (unavail as any).all_day;
                    const isFullDay =
                        unavail.all_day === true ||
                        allDayValue === "1" ||
                        allDayValue === "true" ||
                        allDayValue === 1 ||
                        (!rawStart && !rawEnd);
                    const prettyTimeRange = formatUnavailabilityTimeRange(rawStart, rawEnd);
                    const unavailType = isFullDay ? "Full Day" : "Partial";
                    const employeeName =
                        (typeof unavail.employee?.name === "string" && unavail.employee.name) ||
                        (typeof (unavail as any).employee_name === "string" && (unavail as any).employee_name) ||
                        "";
                    setSelectedUnavailability({
                        unavail,
                        employeeName,
                        timeRange: prettyTimeRange,
                        isFullDay,
                        unavailType,
                    });
                    setIsUnavailabilityModalOpen(true);
                }}
                onOpenShiftClick={
                    showMoreDate
                        ? (item) => {
                              const openShift = item.openShift as ScheduleV3OpenShift;
                              const shiftId =
                                  (openShift as any).os_sch_open_shift_transactions_id ||
                                  (openShift as any).open_shift_id ||
                                  "";
                              setOpenShiftDetailModalShiftId(shiftId || null);
                              setOpenShiftDetailModalInitial(openShift);
                              setOpenShiftDetailModalDay(showMoreDate);
                              setOpenShiftDetailModalGroup(item.group);
                              setOpenShiftDetailModalOpen(true);
                              setShowMoreModal(false);
                          }
                        : undefined
                }
            />
            
            {/* Copied Schedule Indicator */}
            {copiedSchedule && (
                <div className="fixed top-4 right-4 bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-3 z-[1300] max-w-sm">
                    <div className="flex items-start gap-3">
                        <Clipboard className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                        <div className="flex-1">
                            <div className="text-sm font-medium text-blue-900">
                                Schedule Copied
                            </div>
                            <div className="text-xs text-blue-700 mt-1">
                                {copiedSchedule.schedule.employee?.name} - {copiedSchedule.schedule.shift?.shift_name}
                            </div>
                            <div className="text-xs text-blue-600 mt-2">
                                Click on any date cell to paste
                            </div>
                            <div className="text-xs text-gray-500 mt-1 italic">
                                Press ESC or click Done to stop pasting
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button
                                onClick={() => {
                                    setCopiedSchedule(null);
                                    localStorage.removeItem("scheduling_tool_copied_schedule");
                                    if (pasteModeToastIdRef.current) {
                                        toast.dismiss(pasteModeToastIdRef.current);
                                        pasteModeToastIdRef.current = null;
                                    }
                                    toast.info("Paste mode cleared");
                                }}
                                className="text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0"
                                title="Clear copied schedule"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-200">
                        <button
                            onClick={() => {
                                setCopiedSchedule(null);
                                localStorage.removeItem("scheduling_tool_copied_schedule");
                                if (pasteModeToastIdRef.current) {
                                    toast.dismiss(pasteModeToastIdRef.current);
                                    pasteModeToastIdRef.current = null;
                                }
                                toast.success("Done pasting. Paste mode cleared.");
                            }}
                            className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                        >
                            Done Pasting
                        </button>
                    </div>
                </div>
            )}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Day Headers */}
                <div className="relative z-0 flex border-b border-gray-200 bg-gray-50">
                    {/* Grouping Dropdowns Cell */}
                    <div className="w-32 md:w-48 lg:w-80 flex-shrink-0 border-r border-gray-200 p-2">
                        <div className="flex flex-col gap-2">
                            {/* Grouping Type + Sort By (side by side) */}
                            <div className="flex items-center gap-1.5">
                            {getGroupingOptions && onGroupingTypeChange && (
                                <div className="relative flex-1 min-w-0">
                                    <style>{`
                                        .grouping-select__option:hover {
                                            background-color: transparent !important;
                                        }
                                        .grouping-select__option--is-selected:hover {
                                            background-color: #3b82f6 !important;
                                            color: white !important;
                                        }
                                    `}</style>
                                    <Select
                                        value={getGroupingOptions().find(opt => opt.value === groupingType) || null}
                                        onChange={(option) => {
                                            if (option) {
                                                // Close tags dropdown when grouping type changes
                                                if (onAttendanceTagsDropdownToggle && isAttendanceTagsDropdownOpen) {
                                                    onAttendanceTagsDropdownToggle(false);
                                                }
                                                onGroupingTypeChange(option.value as SchedulesV3GroupByType);
                                            }
                                        }}
                                        onMenuOpen={() => {
                                            // Close tags and sort dropdowns when grouping dropdown opens
                                            if (onAttendanceTagsDropdownToggle && isAttendanceTagsDropdownOpen) {
                                                onAttendanceTagsDropdownToggle(false);
                                            }
                                            setIsSortDropdownOpen(false);
                                        }}
                                        options={getGroupingOptions()}
                                        formatOptionLabel={({ label, icon }) => (
                                            <div className="flex items-center gap-2">
                                                <span>{icon}</span>
                                                <span>{label}</span>
                                            </div>
                                        )}
                                        className="text-xs"
                                        classNamePrefix="grouping-select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '32px',
                                                fontSize: '12px',
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                fontSize: '12px',
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                // Handle hover (isFocused) and selected states - no visual change on hover
                                                backgroundColor: state.isSelected
                                                    ? '#3b82f6' // blue when selected, maintain even on hover
                                                    : 'transparent', // transparent always, even on hover
                                                color: state.isSelected ? 'white' : '#374151', // maintain text color on hover
                                                cursor: 'pointer',
                                            }),
                                        }}
                                        menuPortalTarget={document.body}
                                        menuShouldBlockScroll={true}
                                    />
                                </div>
                            )}

                            {/* Sort by - minimalistic icon button beside group by */}
                            {getSortOptions && onSidebarSortChange && sidebarSortBy != null && (
                                <div ref={sortDropdownRef} className="relative flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onAttendanceTagsDropdownToggle && isAttendanceTagsDropdownOpen) {
                                                onAttendanceTagsDropdownToggle(false);
                                            }
                                            setIsSortDropdownOpen((prev) => !prev);
                                        }}
                                        className={`p-1.5 rounded border transition-colors ${
                                            isSortDropdownOpen ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                                        }`}
                                        title="Sort by"
                                        aria-label="Sort by"
                                    >
                                        <ArrowUpDown size={16} />
                                    </button>
                                    {isSortDropdownOpen && (
                                        <div
                                            className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[180px] py-1"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {getSortOptions().map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => {
                                                        onSidebarSortChange(option.value);
                                                        setIsSortDropdownOpen(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                                        sidebarSortBy === option.value
                                                            ? 'bg-blue-600 text-white'
                                                            : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>
                            
                            {/* Tags Dropdown - Only show when grouping by tags */}
                            {groupingType === 'tags' && onTagToggle && onAttendanceTagsDropdownToggle && (
                                <div className="relative">
                                    <button
                                        data-attendance-tags-button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsSortDropdownOpen(false);
                                            onAttendanceTagsDropdownToggle(!isAttendanceTagsDropdownOpen);
                                        }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 border rounded text-xs hover:bg-gray-50 transition-colors ${
                                            selectedTagCode ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                                        }`}
                                    >
                                        <Tag size={12} className={selectedTagCode ? 'text-blue-600' : ''} />
                                        <span className={`flex-1 text-left truncate ${selectedTagCode ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                                            {selectedTagCode 
                                                ? attendanceTags?.find((t: any) => t.field_code === selectedTagCode)?.field_name || 'Tags'
                                                : 'Please select a tag'}
                                        </span>
                                        <ChevronDown size={12} />
                                    </button>
                                    
                                    {isAttendanceTagsDropdownOpen && (
                                        <div 
                                            data-attendance-tags-dropdown
                                            className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-[280px] max-h-[400px] overflow-y-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {isLoadingAttendanceTags ? (
                                                <div className="flex items-center justify-center px-4 py-8">
                                                    <Loader2 className="animate-spin text-purple-600 w-5 h-5" />
                                                    <span className="ml-2 text-sm text-gray-600">Loading tags...</span>
                                                </div>
                                            ) : attendanceTags && attendanceTags.length > 0 ? (
                                                <div className="py-2">
                                                    {/* Individual tags - filter out Text type tags, only show Dropdown type */}
                                                    {attendanceTags
                                                        .filter((tag) => {
                                                            // Only show tags with field_type "Dropdown", exclude "Text" type
                                                            const fieldType = tag.field_type?.toLowerCase();
                                                            return fieldType === 'dropdown';
                                                        })
                                                        .map((tag) => {
                                                        const isSelected = selectedTagCode === tag.field_code;
                                                        return (
                                                            <label
                                                                key={tag.field_code}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onTagToggle(tag.field_code);
                                                                    onAttendanceTagsDropdownToggle?.(false);
                                                                }}
                                                                className={`flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                                                                    isSelected ? 'bg-blue-50' : ''
                                                                }`}
                                                            >
                                                                <div className="flex items-center justify-center w-5 h-5 border-2 rounded-full border-gray-300 flex-shrink-0">
                                                                    {isSelected && (
                                                                        <div className="w-3 h-3 rounded-full bg-blue-600" />
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {tag.field_name}
                                                                    </div>
                                                                    {tag.field_description && (
                                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                                            {tag.field_description}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <input
                                                                    type="radio"
                                                                    name="attendance-tag-selection"
                                                                    checked={isSelected}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        onTagToggle(tag.field_code);
                                                                    }}
                                                                    className="sr-only"
                                                                />
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="px-4 py-8 text-center text-sm text-gray-500">
                                                    No attendance tags available
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Date Columns */}
                    {displayDays.map((day, index) => {
                        const isToday = isSameDay(day, new Date());
                        return (
                            <div
                                key={index}
                                className={`flex-1 ${
                                    viewType === "daily" ? "p-2 md:p-4" : "p-1.5 md:p-3"
                                } text-center border-r border-gray-200 last:border-r-0 ${
                                    isToday ? "bg-purple-50 border-purple-200" : ""
                                }`}
                            >
                                <div
                                    className={`text-[10px] md:text-sm font-medium ${
                                        isToday ? "text-purple-600" : "text-gray-900"
                                    }`}
                                >
                                    <span className="hidden md:inline">{format(day, "EEE, d")}</span>
                                    <span className="md:hidden">{format(day, "EEE")}</span>
                                    <div className="md:hidden text-[9px] font-normal">{format(day, "d")}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Schedule Rows */}
                <div className="relative z-10 flex-1 overflow-y-auto">
                    {(() => {
                        const groupsToShow = groups.length === 0 ? getPlaceholderGroups : groups;

                        // Extract holidays group
                        const holidaysGroup = groupsToShow.find((g) => g.group_id === "global_holiday");
                        const regularGroups = groupsToShow.filter((g) => g.group_id !== "global_holiday");

                        // Show "Please select a tag" message when grouping by tags and no tag is selected
                        if (groupingType === 'tags' && !selectedTagCode) {
                            return (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                    <div className="text-center">
                                        <p className="text-lg font-medium">Please select a tag</p>
                                    </div>
                                </div>
                            );
                        }

                        if (regularGroups.length === 0 && !holidaysGroup) {
                            return (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                    <div className="text-center">
                                        <p className="text-lg font-medium">No data available</p>
                                        <p className="text-sm mt-2">
                                            Please ensure departments, sites, employees, or job codes are loaded
                                        </p>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <>
                                {/* Holidays Row - First Row */}
                                {holidaysGroup && (
                                    <div className="flex border-b border-gray-200 min-h-[80px] md:min-h-[120px]">
                                        {/* Holidays Group Name Column */}
                                        <div className="w-32 md:w-48 lg:w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50 p-2 md:p-4">
                                            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
                                                <span className="font-medium text-gray-900 text-[10px] md:text-base truncate">
                                                    {holidaysGroup.group_name}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Holidays Columns */}
                                        {displayDays.map((day, dayIndex) => {
                                            if (!isHolidaysRowExpanded) {
                                                return (
                                                    <div
                                                        key={dayIndex}
                                                        className="flex-1 border-r border-gray-200 last:border-r-0 p-1 md:p-2 min-w-[60px] md:min-w-0"
                                                    />
                                                );
                                            }

                                            const dateStr = format(day, "yyyy-MM-dd");
                                            const dateData = holidaysGroup.dates[dateStr];
                                            const holidaysInCell = dateData?.holiday || [];

                                            return (
                                                <div
                                                    key={dayIndex}
                                                    className="flex-1 border-r border-gray-200 last:border-r-0 p-1 md:p-2 space-y-0.5 md:space-y-1 min-w-[60px] md:min-w-0"
                                                >
                                                    {holidaysInCell.map((holiday: ScheduleV3Holiday, index: number) => {
                                                        return (
                                                            <div
                                                                key={`holiday-${index}`}
                                                                className="p-1 md:p-2 rounded bg-red-500 text-white text-[9px] md:text-xs relative z-10 hover:z-30 focus-within:z-30 group cursor-pointer hover:bg-red-600 transition-colors"
                                                                title={holiday.title}
                                                            >
                                                                <div className="font-medium truncate">{holiday.title}</div>

                                                                {/* Hover tooltip - Desktop only */}
                                                                <div className={`hidden md:block absolute ${getTooltipPlacementClasses(true)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-40 max-w-xs`}>
                                                                    <div className="font-medium">{holiday.title}</div>
                                                                    {holiday.earning_code && (
                                                                        <div className="text-gray-300 text-[10px]">{holiday.earning_code}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Regular Groups */}
                                {regularGroups.map((group, groupIndex) => {
                            const groupRef = group;
                            const totalHours = calculateGroupHours(group);
                            const displayGroupName = getDisplayGroupName(group);
                            const shouldOpenTooltipsBelow = groupIndex === 0;

                            return (
                                <div
                                    key={group.group_id}
                                    className="flex border-b border-gray-200 min-h-[80px] md:min-h-[120px]"
                                >
                                    {/* Group Name Column */}
                                    <div className="w-32 md:w-48 lg:w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50 p-2 md:p-4">
                                        <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
                                            <div className="hidden md:block">{getGroupIconForGroup(group)}</div>
                                            <span className="font-medium text-gray-900 text-[10px] md:text-base truncate">
                                                {displayGroupName}
                                            </span>
                                        </div>
                                        <div className="text-[9px] md:text-sm text-gray-500">
                                            {totalHours.toFixed(2)} Hrs
                                        </div>
                                    </div>

                                    {/* Schedule Columns */}
                                    {displayDays.map((day, dayIndex) => {
                                        const daySchedules = getSchedulesForGroupAndDay(group, day);
                                        const dateStr = format(day, "yyyy-MM-dd");
                                        const dateData = group.dates[dateStr];

                                        // Calculate total items and visible items for weekly/daily view
                                        const allSchedulesInCell = daySchedules;
                                        const allTimeoffsInCell = dateData?.timeoff || [];
                                        const allOpenShiftsInCell = dateData?.open_shifts || [];
                                        const allUnavailabilityInCell = dateData?.unavailability || [];
                                        const totalItemsInCell =
                                            allSchedulesInCell.length +
                                            allTimeoffsInCell.length +
                                            allOpenShiftsInCell.length +
                                            allUnavailabilityInCell.length;
                                        const hasMoreItemsInCell = totalItemsInCell > MAX_VISIBLE_ITEMS_WEEKLY;
                                        const remainingCountInCell = totalItemsInCell - MAX_VISIBLE_ITEMS_WEEKLY;

                                        const visibleSchedulesInCell = hasMoreItemsInCell
                                            ? allSchedulesInCell.slice(
                                                  0,
                                                  Math.min(MAX_VISIBLE_ITEMS_WEEKLY, allSchedulesInCell.length)
                                              )
                                            : allSchedulesInCell;
                                        const remainingSlotsInCell = hasMoreItemsInCell
                                            ? MAX_VISIBLE_ITEMS_WEEKLY - visibleSchedulesInCell.length
                                            : 0;
                                        const visibleTimeoffsInCell = hasMoreItemsInCell
                                            ? allTimeoffsInCell.slice(0, Math.max(0, remainingSlotsInCell))
                                            : allTimeoffsInCell;
                                        const remainingSlotsAfterTimeoffs = hasMoreItemsInCell
                                            ? Math.max(0, MAX_VISIBLE_ITEMS_WEEKLY - visibleSchedulesInCell.length - visibleTimeoffsInCell.length)
                                            : Infinity;
                                        const visibleOpenShiftsInCell = hasMoreItemsInCell
                                            ? allOpenShiftsInCell.slice(0, remainingSlotsAfterTimeoffs)
                                            : allOpenShiftsInCell;
                                        const remainingSlotsAfterOpenShifts = hasMoreItemsInCell
                                            ? Math.max(0, MAX_VISIBLE_ITEMS_WEEKLY - visibleSchedulesInCell.length - visibleTimeoffsInCell.length - visibleOpenShiftsInCell.length)
                                            : Infinity;
                                        const visibleUnavailabilityInCell = hasMoreItemsInCell
                                            ? allUnavailabilityInCell.slice(0, remainingSlotsAfterOpenShifts)
                                            : allUnavailabilityInCell;
                                        const cellKey = getCellHoverKey(day, groupRef);
                                        const isHovered = hoveredCell === cellKey;
                                        const showPasteHover = copiedSchedule && isHovered && canPasteIntoCell(day);

                                        return (
                                            <div
                                                key={dayIndex}
                                                className="relative flex-1 border-r border-gray-200 last:border-r-0 p-1 md:p-2 space-y-0.5 md:space-y-1 min-w-[60px] md:min-w-0 overflow-visible"
                                                onMouseEnter={() => {
                                                    if (SchedulingToolUtils.canEditDate(day, schedulingConfig) && hasScheduleToolPrivilege) {
                                                        setHoveredCell(cellKey);
                                                    }
                                                }}
                                                onMouseLeave={() => setHoveredCell(null)}
                                                onClick={(e) => {
                                                    tryHandlePasteCellClick(day, groupRef, e);
                                                }}
                                            >
                                                {showPasteHover && (
                                                    <div className="pointer-events-none absolute inset-1 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-blue-400 bg-blue-100/80">
                                                        <span className="rounded-full bg-blue-600 px-2 py-1 text-[10px] font-medium text-white shadow-sm md:text-xs">
                                                            Paste here
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Schedules */}
                                                {visibleSchedulesInCell.map((schedule, scheduleIndex) => {
                                                    const scheduleColor = schedule.shift?.shift_color || "#4CAF50";
                                                    const scheduleConflicts = schedule.os_emp_schedules_id != null
                                                        ? (conflictsByScheduleId.get(schedule.os_emp_schedules_id) ?? []) : [];
                                                    const conflictSeverity = getConflictSeverity(scheduleConflicts);
                                                    const timeRange = resolveShiftTimeRange(schedule.shift?.start_time, schedule.shift?.end_time);
                                                    const displayTimeRange = timeRange && timeRange !== 'N/A' ? timeRange : '';
                                                    
                                                    // Format card content based on config
                                                    const cardFields = displayConfig.schedule.card;
                                                    const cardContent = formatCardContent(cardFields, { schedule, timeRange: displayTimeRange }, 'schedule', group.group_name);
                                                    
                                                    // Format hover content
                                                    const hoverContent = formatHoverContent(displayConfig.schedule.hover, { schedule, timeRange: displayTimeRange }, 'schedule', group.group_name);
                                                    const tooltipSuppressionKey = schedule.os_emp_schedules_id != null
                                                        ? `grid-schedule-${schedule.os_emp_schedules_id}`
                                                        : `grid-schedule-${format(day, "yyyy-MM-dd")}-${scheduleIndex}`;

                                                    return (
                                                        <div
                                                            key={scheduleIndex}
                                                            className={`p-1 md:p-2 rounded text-white text-[9px] md:text-xs relative z-10 hover:z-30 focus-within:z-30 group cursor-pointer hover:opacity-90 transition-opacity overflow-visible ${SchedulingToolUtils.getPublishStatusInfo(schedule)?.isUnpublished ? 'border-2 border-dashed border-white/70' : ''} ${conflictSeverity ? conflictSeverity.ring : ''}`}
                                                            style={{ backgroundColor: scheduleColor }}
                                                            onClick={(e) => {
                                                                if (tryHandlePasteCellClick(day, group, e)) {
                                                                    return;
                                                                }
                                                                e.stopPropagation(); // Prevent cell click from firing
                                                                handleScheduleClick(schedule, day, group);
                                                            }}
                                                            onMouseLeave={() => {
                                                                setSuppressedShiftTooltipKey((current) =>
                                                                    current === tooltipSuppressionKey ? null : current
                                                                );
                                                            }}
                                                        >
                                                            <div className="flex items-start justify-between gap-1">
                                                                <div className="flex-1 min-w-0">
                                                                    {cardFields.map((field, i) => {
                                                                        const content = getFieldValue(field, { schedule, timeRange, groupName: group.group_name }, 'schedule');
                                                                        if (!content) return null; // Skip empty fields
                                                                        const isEmployeeName = field === 'employeeName';
                                                                        return (
                                                                            <div
                                                                                key={field}
                                                                                className={`truncate ${
                                                                                    i === 0 ? 'font-medium text-[9px] md:text-xs' :
                                                                                    'text-[8px] md:text-[10px]'
                                                                                } ${isEmployeeName ? 'hidden md:block' : ''}`}
                                                                            >
                                                                                {content}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {/* Mobile fallback for employee name */}
                                                                    {cardFields.includes('employeeName') && (
                                                                        <div className="truncate md:hidden text-[8px]">
                                                                            {(schedule.employee?.name || "Unknown").split(" ")[0]}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div
                                                                    className="flex items-center gap-1 flex-shrink-0"
                                                                    onMouseEnter={() => setSuppressedShiftTooltipKey(tooltipSuppressionKey)}
                                                                    onMouseLeave={() => {
                                                                        setSuppressedShiftTooltipKey((current) =>
                                                                            current === tooltipSuppressionKey ? null : current
                                                                        );
                                                                    }}
                                                                >
                                                                    {/* Conflict Badge */}
                                                                    {conflictSeverity && scheduleConflicts.length > 0 && (
                                                                        <div
                                                                            className="relative"
                                                                            onClick={(e) => {
                                                                                if (tryHandlePasteCellClick(day, group, e)) return;
                                                                                e.stopPropagation();
                                                                            }}
                                                                        >
                                                                            <div
                                                                                className={`inline-flex items-center relative z-10 group/conflict rounded-full p-0.5 group-hover/conflict:z-40 ${conflictSeverity.badge}`}
                                                                            >
                                                                                <AlertTriangle size={12} className="w-2 h-2 md:w-3 md:h-3" />
                                                                                <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow, "center")} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover/conflict:opacity-100 pointer-events-none z-40 transition-opacity min-w-max max-w-[180px]`}>
                                                                                    {scheduleConflicts.map((c, i) => (
                                                                                        <div key={i} className="whitespace-normal">{c.message}</div>
                                                                                    ))}
                                                                                    <div className={getTooltipArrowClasses(shouldOpenTooltipsBelow)}>
                                                                                        <div className={getTooltipArrowInnerClasses(shouldOpenTooltipsBelow)}></div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {/* Publish Status Icon */}
                                                                    {(() => {
                                                                        const publishInfo = SchedulingToolUtils.getPublishStatusInfo(schedule);
                                                                        if (!publishInfo) return null;

                                                                        const IconComponent = publishInfo.iconName === 'XCircle' ? XCircle :
                                                                                               publishInfo.iconName === 'CheckCircle2' ? CheckCircle2 : Clock;

                                                                        return (
                                                                            <div
                                                                                className="relative"
                                                                                onClick={(e) => {
                                                                                    if (tryHandlePasteCellClick(day, group, e)) {
                                                                                        return;
                                                                                    }
                                                                                    e.stopPropagation();
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    className={`inline-flex items-center ${publishInfo.iconColor} relative z-10 group/icon group-hover/icon:z-40 ${publishInfo.isUnpublished ? 'bg-orange-500 rounded-full p-0.5' : ''}`}
                                                                                >
                                                                                    <IconComponent size={12} className="w-2 h-2 md:w-3 md:h-3" />
                                                                                    {/* Tooltip on hover - only shows when hovering over the icon */}
                                                                                    <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow, "center")} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover/icon:opacity-100 pointer-events-none whitespace-nowrap z-40 transition-opacity`}>
                                                                                        {publishInfo.tooltip}
                                                                                        <div className={getTooltipArrowClasses(shouldOpenTooltipsBelow)}>
                                                                                            <div className={getTooltipArrowInnerClasses(shouldOpenTooltipsBelow)}></div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>

                                                            {/* Hover tooltip - Desktop only */}
                                                            {hoverContent && suppressedShiftTooltipKey !== tooltipSuppressionKey && (
                                                                <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 group-hover/conflict:opacity-0 group-hover/icon:opacity-0 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                                    <div className="whitespace-normal">{hoverContent}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {/* Timeoff */}
                                                {visibleTimeoffsInCell.map((timeoff, index) => {
                                                    // Check if this is a partial day request based on hours
                                                    // If hours is set and less than 8 (typical work day), it's partial
                                                    const isPartialDay = timeoff.hours && timeoff.hours > 0 && timeoff.hours < 8;
                                                    const effectiveRequestId = getEffectiveTimeoffRequestId(timeoff);
                                                    const hasRequestId = effectiveRequestId != null;
                                                    
                                                    // Try to get time range from details_json.days if available
                                                    let timeRangeFromDetails: string | null = null;
                                                    if (timeoff.details_json?.days && Array.isArray(timeoff.details_json.days) && timeoff.details_json.days.length > 0) {
                                                        const dayDetails = timeoff.details_json.days[0];
                                                        if (dayDetails.start_time && dayDetails.end_time && 
                                                            dayDetails.start_time !== "00:00:00" && dayDetails.end_time !== "00:00:00") {
                                                            // Format the time range
                                                            const startTime = dayDetails.start_time.substring(0, 5);
                                                            const endTime = dayDetails.end_time.substring(0, 5);
                                                            timeRangeFromDetails = `${startTime} - ${endTime}`;
                                                        }
                                                    }
                                                    
                                                    const displayRange = (() => {
                                                        // When show_time is true, always show time range from start_datetime/end_datetime
                                                        if (timeoff.show_time === true && timeoff.start_datetime && timeoff.end_datetime) {
                                                            const formatted = DateTimeUtil.formatElegantTimeRange(
                                                                timeoff.start_datetime,
                                                                timeoff.end_datetime
                                                            );
                                                            if (formatted && formatted !== "N/A") {
                                                                return formatted;
                                                            }
                                                            // Fallback: extract time parts if formatElegantTimeRange fails
                                                            const startPartRaw = DateTimeUtil.getTime24HourFormat(timeoff.start_datetime);
                                                            const endPartRaw = DateTimeUtil.getTime24HourFormat(timeoff.end_datetime);
                                                            const startPart = startPartRaw === "Invalid time" ? "" : startPartRaw;
                                                            const endPart = endPartRaw === "Invalid time" ? "" : endPartRaw;
                                                            if (startPart && endPart) return `${startPart} - ${endPart}`;
                                                        }
                                                        // When show_time is false: track_time_in "Days" + hours 1/0.5 → Full day / Half day
                                                        if (timeoff.show_time === false) {
                                                            const trackInDays = (timeoff as any).track_time_in === "Days";
                                                            if (trackInDays && (timeoff.hours == 1 || Number(timeoff.hours) === 1)) return "Full day";
                                                            if (trackInDays && (timeoff.hours == 0.5 || Number(timeoff.hours) === 0.5)) return "Half day";
                                                            return `${timeoff.hours}h`;
                                                        }
                                                        // If we have time range from details_json, use it
                                                        if (timeRangeFromDetails) {
                                                            return timeRangeFromDetails;
                                                        }
                                                        
                                                        // If it's a partial day (has hours < 8), show hours instead of "Full day"
                                                        if (isPartialDay && timeoff.hours) {
                                                            return `${timeoff.hours}h`;
                                                        }
                                                        
                                                        // Otherwise, try to format from datetime strings
                                                        if (timeoff.start_datetime && timeoff.end_datetime) {
                                                            const formatted = DateTimeUtil.formatElegantTimeRange(
                                                                timeoff.start_datetime,
                                                                timeoff.end_datetime
                                                            );
                                                            if (formatted === "Full day" && (isPartialDay && timeoff.hours)) {
                                                                return `${timeoff.hours}h`;
                                                            }
                                                            if (formatted && formatted !== "N/A") {
                                                                return formatted;
                                                            }
                                                        }
                                                        
                                                        // Fallback: if show_time is true but no time range, show hours
                                                        if (timeoff.show_time === true) {
                                                            return `${timeoff.hours}h`;
                                                        }
                                                        
                                                        return "Time Off";
                                                    })();
                                                    const shouldShowTime = timeoff.show_time === true;
                                                    // isFullDay should be false if hours indicate partial day or if show_time is true with hours
                                                    const isFullDay = displayRange === "Full day" && !isPartialDay && !timeoff.show_time;

                                                    // Build tooltip text with time information
                                                    const buildTooltipText = () => {
                                                        const parts: string[] = [];
                                                        parts.push(timeoff.earnings_code_name);
                                                        
                                                        // Add time range if available (include Full day / Half day from track_time_in)
                                                        if (timeRangeFromDetails) {
                                                            parts.push(timeRangeFromDetails);
                                                        } else if (displayRange && displayRange !== "Time Off") {
                                                            parts.push(displayRange);
                                                        }
                                                        
                                                        // Add hours if available and not already shown in time range / Full day / Half day
                                                        if (timeoff.hours && timeoff.hours > 0) {
                                                            if (!timeRangeFromDetails && displayRange !== `${timeoff.hours}h` && displayRange !== "Full day" && displayRange !== "Half day") {
                                                                parts.push(`${timeoff.hours} hours`);
                                                            }
                                                        }
                                                        
                                                        return parts.join(" - ");
                                                    };

                                                    // Check status - handle both string and null/undefined cases
                                                    const statusValue = timeoff.status;
                                                    const isPending = statusValue && String(statusValue).toUpperCase().trim() === "PENDING";

                                                    return (
                                                        <div
                                                            key={`timeoff-${index}`}
                                                            className={`p-1 md:p-2 rounded bg-orange-500 text-white text-[9px] md:text-xs relative z-10 hover:z-30 focus-within:z-30 group overflow-visible ${isPending ? 'pending-stripe' : ''} ${hasRequestId ? 'cursor-pointer hover:bg-orange-600' : ''}`}
                                                            title={buildTooltipText()}
                                            onClick={hasRequestId ? (e) => {
                                                if (tryHandlePasteCellClick(day, group, e)) {
                                                    return;
                                                }
                                                e.stopPropagation();
                                                setSelectedTimeoffForModal({
                                                    timeoff,
                                                    groupName: group.group_name,
                                                    time_off_request_id: effectiveRequestId,
                                                });
                                                setIsTimeoffDetailModalOpen(true);
                                            } : undefined}
                                                        >
                                                            <div className="font-medium truncate">
                                                                {timeoff.employee?.name || "Unknown"}
                                                            </div>
                                                            <div className="truncate text-[8px] md:text-[10px] opacity-90">
                                                                {timeoff.earnings_code_name}
                                                                {shouldShowTime && displayRange ? ` ${displayRange}` : ""}
                                                                {timeoff.hours && timeoff.hours > 0 ? ` (${timeoff.hours}h)` : ""}
                                                            </div>

                                                            {/* Hover tooltip - Desktop only */}
                                                            <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                                <div className="font-medium">{buildTooltipText()}</div>
                                                                {isPending && (
                                                                    <div className="text-yellow-300 text-[10px] mt-1">Pending approval</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Open Shifts */}
                                                {visibleOpenShiftsInCell.map((openShift, index) => {
                                                    const startTime = openShift.shift_details?.shift_start_time?.substring(0, 5) || "";
                                                    const endTime = openShift.shift_details?.shift_end_time?.substring(0, 5) || "";
                                                    const timeRange = startTime && endTime ? `${startTime}-${endTime}` : "";
                                                    const openShiftStatusInfo = SchedulingToolUtils.getOpenShiftStatusInfo(openShift as any);
                                                    const hoverContent = formatHoverContent(
                                                        displayConfig.openShift.hover,
                                                        { openShift, timeRange },
                                                        'openShift',
                                                        group.group_name
                                                    );

                                                    return (
                                                        <div
                                                            key={`open-${index}`}
                                                            className="p-1 md:p-2 rounded border-2 border-dashed border-purple-400 bg-purple-50 text-purple-700 text-[9px] md:text-xs cursor-pointer hover:bg-purple-100 transition-colors relative z-10 hover:z-30 focus-within:z-30 group overflow-visible"
                                                            onClick={(e) => {
                                                                if (tryHandlePasteCellClick(day, group, e)) {
                                                                    return;
                                                                }
                                                                e.stopPropagation();
                                                                handleOpenShiftClick(openShift, day, group);
                                                            }}
                                                        >
                                                            <div className="font-medium truncate">
                                                                {timeRange || "Open Shift"}
                                                            </div>
                                                            <div className="truncate hidden md:flex md:items-center md:gap-1">
                                                                <span>{openShift.shift_details?.shift_name || 'Open Shift'}</span>
                                                                {openShiftStatusInfo && (
                                                                    <span
                                                                        className={`inline-flex items-center ${openShiftStatusInfo.isDraft ? "bg-amber-100 rounded-full p-0.5" : ""} ${openShiftStatusInfo.iconColor}`}
                                                                        title={openShiftStatusInfo.tooltip}
                                                                    >
                                                                        {openShiftStatusInfo.iconName === "Pencil" ? (
                                                                            <Pencil size={10} />
                                                                        ) : (
                                                                            <CheckCircle2 size={10} />
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="truncate md:hidden text-[8px]">
                                                                {(openShift.shift_details?.shift_name || 'Open').split(" ")[0]}
                                                            </div>

                                                            {/* Hover tooltip - Desktop only */}
                                                            <div className={`hidden md:block absolute ${getTooltipPlacementClasses(shouldOpenTooltipsBelow)} px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-40 max-w-xs`}>
                                                                <div className="whitespace-normal">{hoverContent || "Open Shift"}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Unavailability */}
                                                {visibleUnavailabilityInCell.map((unavail, index) => {
                                                    const rawStart =
                                                        (unavail as any)?.start_time ??
                                                        (unavail as any)?.from_time ??
                                                        null;
                                                    const rawEnd =
                                                        (unavail as any)?.end_time ??
                                                        (unavail as any)?.to_time ??
                                                        null;
                                                    
                                                    // Determine if it's a full day or partial unavailability
                                                    const allDayValue = (unavail as any).all_day;
                                                    const isFullDay = unavail.all_day === true || 
                                                                     allDayValue === "1" || 
                                                                     allDayValue === "true" ||
                                                                     allDayValue === 1 ||
                                                                     (!rawStart && !rawEnd);
                                                    
                                                    // Pretty print time range for display
                                                    const prettyTimeRange = formatUnavailabilityTimeRange(rawStart, rawEnd);
                                                    const unavailType = isFullDay ? "Full Day" : "Partial";
                                                    
                                                    const employeeName =
                                                        (typeof unavail.employee?.name === "string" &&
                                                            unavail.employee.name) ||
                                                        (typeof (unavail as any).employee_name === "string" &&
                                                            (unavail as any).employee_name) ||
                                                        "";
                                                    
                                                    // For employee grouping, show simplified format
                                                    // Otherwise show time range if not full day, or "Full Day" if it is
                                                    const displayText = groupingType === 'employee' 
                                                        ? `Unavail. (${unavailType})`
                                                        : (isFullDay ? unavailType : prettyTimeRange);
                                                    
                                                    // Check status - handle both string and null/undefined cases
                                                    const unavailStatus = (unavail as any).status;
                                                    const isPending = unavailStatus && String(unavailStatus).toUpperCase().trim() === "PENDING";
                                                    // Tooltip for hover (web) - no reason shown, can view on click
                                                    const tooltipText = `Unavailable — ${unavailType} | ${prettyTimeRange}${isPending ? ' | Pending Approval' : ''}`;

                                                    return (
                                                        <div
                                                            key={`unavail-${index}`}
                                                            className={`p-1 md:p-2 rounded bg-gray-400 text-white text-[9px] md:text-xs cursor-pointer hover:bg-gray-500 transition-colors relative z-10 hover:z-30 focus-within:z-30 overflow-visible ${isPending ? 'pending-stripe' : ''}`}
                                                            title={tooltipText}
                                                            onClick={(e) => {
                                                                if (tryHandlePasteCellClick(day, group, e)) {
                                                                    return;
                                                                }
                                                                e.stopPropagation();
                                                                setSelectedUnavailability({
                                                                    unavail,
                                                                    employeeName,
                                                                    timeRange: prettyTimeRange,
                                                                    isFullDay,
                                                                    unavailType
                                                                });
                                                                setIsUnavailabilityModalOpen(true);
                                                            }}
                                                        >
                                                            <div className="font-medium truncate">{displayText}</div>
                                                            {/* Show employee name only when NOT grouping by employee */}
                                                            {groupingType !== 'employee' && employeeName && (
                                                                <div className="truncate hidden md:block">
                                                                    {employeeName}
                                                                </div>
                                                            )}
                                                            {groupingType !== 'employee' && employeeName && (
                                                                <div className="truncate md:hidden text-[8px]">
                                                                    {employeeName.split(" ")[0]}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {/* Show More Button - positioned after all visible items */}
                                                {hasMoreItemsInCell && (
                                                    <button
                                                        onClick={(e) => {
                                                            if (tryHandlePasteCellClick(day, groupRef, e)) {
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            const allSchedulesForModal = allSchedulesInCell.map(
                                                                (schedule) => ({
                                                                    schedule,
                                                                    groupName: group.group_name,
                                                                    group,
                                                                })
                                                            );
                                                            const allTimeoffsForModal = allTimeoffsInCell.map(
                                                                (timeoff) => ({
                                                                    timeoff,
                                                                    groupName: group.group_name,
                                                                    group,
                                                                })
                                                            );
                                                            const allOpenShiftsForModal = allOpenShiftsInCell.map(
                                                                (openShift) => ({
                                                                    openShift,
                                                                    groupName: group.group_name,
                                                                    group,
                                                                })
                                                            );
                                                            const allUnavailabilityForModal = allUnavailabilityInCell.map(
                                                                (unavailability) => ({
                                                                    unavailability,
                                                                    groupName: group.group_name,
                                                                    group,
                                                                })
                                                            );
                                                            setShowMoreItems({
                                                                schedules: allSchedulesForModal,
                                                                timeoffs: allTimeoffsForModal,
                                                                openShifts: allOpenShiftsForModal,
                                                                unavailability: allUnavailabilityForModal,
                                                            });
                                                            setShowMoreDate(day);
                                                            setShowMoreModal(true);
                                                        }}
                                                        className="w-full px-1 md:px-2 py-1 md:py-1.5 text-[9px] md:text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                                                    >
                                                        <span className="hidden md:inline">
                                                            + {remainingCountInCell} more
                                                        </span>
                                                        <span className="md:hidden">+{remainingCountInCell}</span>
                                                    </button>
                                                )}

                                                {/* Add Schedule Button - Only show if user has privileges */}
                                                {SchedulingToolUtils.canEditDate(day, schedulingConfig) && hasScheduleToolPrivilege && (
                                                    <button
                                                        onClick={(e) => {
                                                            if (tryHandlePasteCellClick(day, groupRef, e)) {
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            handleCellClick(day, groupRef, e);
                                                        }}
                                                        className="w-full p-2 border-2 border-dashed border-gray-300 rounded text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors text-xs"
                                                    >
                                                        +
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                            </>
                        );
                    })()}
                </div>

                {/* Hours Summary Row */}
                <div className="flex border-t border-gray-200 bg-gray-50">
                    <div className="w-80 flex-shrink-0 border-r border-gray-200 p-4">
                        <div className="font-medium text-gray-900">Hours</div>
                    </div>
                    {displayDays.map((day: Date, index: number) => {
                        const dayStr = format(day, "yyyy-MM-dd");
                        let totalHours = 0;

                        groups.forEach((group) => {
                            const dateData = group.dates[dayStr];
                            if (dateData?.schedules) {
                                dateData.schedules.forEach((schedule) => {
                                    totalHours += getShiftDurationHours(
                                        schedule.shift?.start_time,
                                        schedule.shift?.end_time
                                    );
                                });
                            }
                        });

                        return (
                            <div
                                key={index}
                                className="flex-1 border-r border-gray-200 last:border-r-0 p-4 text-center"
                            >
                                <div className="font-medium text-gray-900">{totalHours.toFixed(2)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default SchedulingToolWeeklyGrid;
